import { useState, useMemo, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { LoadingCard } from '@/components/ui/loading';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { RichTextEditor } from '@/components/RichTextEditor';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getCustomers, getProducts, getCategories, createInvoice, updateInvoice, getCompany, createCustomer, getInvoices, getInvoice } from '@/lib/api';
import type { CompanySettings, Invoice, Customer, InvoiceActivity, Product, Category } from '../types/firebase';
import { useAuth } from '@/components/AuthProvider';
import { Plus, Trash2, Mail, Phone, ArrowLeft, ArrowRight, Loader2 } from 'lucide-react';
import { showNotification } from '@/lib/generalNotifications';
import { Card } from '@/components/ui/card';
import { generateInvoiceNumber, generateRecurringTemplateNumber, getTodayInLocalTimezone, formatDateToLocalTimezone } from '@/lib/utils';
import { cn } from "@/lib/utils"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { InvoiceActivityTimeline } from '@/components/InvoiceActivityTimeline';
import { v4 as uuidv4 } from 'uuid';
import { Timestamp } from 'firebase/firestore';
import { doc, getDoc, addDoc, collection, serverTimestamp, query, getDocs, where, updateDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { generatePaymentKey } from '@/lib/utils';
import { PaymentMethodsSection } from "@/components/invoices/PaymentMethodsSection";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Switch } from "@/components/ui/switch";
import { InfoCircledIcon } from "@radix-ui/react-icons";
import { AddEditProductDialog } from '@/components/dialogs/AddEditProductDialog';
import { AddCategoryDialog } from '@/components/dialogs/AddCategoryDialog';
import { AddCustomerDialog } from '@/components/dialogs/AddCustomerDialog';
import { CustomerSearch } from '@/components/ui/customer-search';
import { ProductSearch } from '@/components/ui/product-search';
import { CategorySearch } from '@/components/ui/category-search';
import { isAchPaymentAvailable } from '@/utils/paymentGateways';

interface LineItem {
  id: string;
  categoryId?: string;
  categoryName: string;
  productId?: string;
  productName: string;
  description: string;
  quantity: number;
  unitPrice: number;
  total: number;
  isTaxable: boolean;
}

type PaymentTerms = 'due_on_receipt' | 'net_15' | 'net_30' | 'net_60';

// TEMP: Extend recurring frequency type for testing
import type { Invoice as BaseInvoice } from '../types/firebase';

type RecurringFrequency = 'weekly' | 'monthly' | 'quarterly' | 'annual' | 'every_minute';

interface RecurringFrequencyDetails {
  type: 'weekly' | 'monthly' | 'yearly' | ''; // Removed 'daily'
  every: number;
  dayOfWeek?: 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday';
  dayOfMonth?: number | 'first' | '15th' | 'last';
  duePreference: 'specific_date' | 'first_of_month' | '15th_of_month' | 'anniversary' | 'day_of_week';
  invoiceIssueDaysBeforeDue: number;
}

interface InvoiceFormData {
  customerId: string;
  invoiceNumber: string;
  poNumber: string;
  date: string;
  dueDate: string;
  paymentTerms: PaymentTerms;
  paymentMethods: {
    credit_card: boolean;
    paypal: boolean;
    ach: boolean;
    check: boolean;
  };
  autoPayEnabled: boolean;  // Add autoPayEnabled field
  items: LineItem[];
  notes: string;
  footerText: string;
  invoiceType: 'standard' | 'recurring' | 'installment';
  nextGenerationDate?: Date | null;
  recurringDetails?: {
    frequency: RecurringFrequency;
    endDate?: Date | null;
    remainingOccurrences?: number | null;
    isActive: boolean;
    frequencyDetails?: RecurringFrequencyDetails;
    enableACH?: boolean;
  } | null;
  scheduledSendDate?: string | null;
}

// Use local date string in 'YYYY-MM-DD' format for all date fields
const getLocalDateString = (date: Date) => {
  // Force the date to be interpreted in local timezone to avoid UTC conversion issues
  const localDate = new Date(date);
  localDate.setMinutes(localDate.getMinutes() + localDate.getTimezoneOffset());
  const year = localDate.getFullYear();
  const month = String(localDate.getMonth() + 1).padStart(2, '0');
  const day = String(localDate.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Add a function to correctly parse YYYY-MM-DD format dates for display
const parseDatePreservingDay = (dateStr: string) => {
  if (!dateStr) return new Date();
  
  // Parse the YYYY-MM-DD format manually to ensure the day is preserved
  const [year, month, day] = dateStr.split('-').map(Number);
  
  // Create date using local timezone (month is 0-indexed in JavaScript)
  return new Date(year, month - 1, day);
};

const PAYMENT_TERMS = [
  { value: 'due_on_receipt', label: 'Due on Receipt' },
  { value: 'net_15', label: 'Net 15' },
  { value: 'net_30', label: 'Net 30' },
  { value: 'net_60', label: 'Net 60' },
];

const CLEAR_SELECT_VALUE = '__clear';

const calculateDueDate = (date: string, terms: string): string => {
  // Parse as local date
  const [year, month, day] = date.split('-').map(Number);
  const baseDate = new Date(year, month - 1, day);
  switch (terms) {
    case 'net_15':
      baseDate.setDate(baseDate.getDate() + 15);
      break;
    case 'net_30':
      baseDate.setDate(baseDate.getDate() + 30);
      break;
    case 'net_60':
      baseDate.setDate(baseDate.getDate() + 60);
      break;
    default:
      // due_on_receipt - same as invoice date
      break;
  }
  return getLocalDateString(baseDate);
};

// Update getInitialFormData to use searchParams from props
const getInitialFormData = (companySettings: CompanySettings | null | undefined, searchParams: URLSearchParams): InvoiceFormData => {
  // Ensure we get today's date properly in local timezone
  const today = getLocalDateString(new Date());
  const typeFromUrl = searchParams.get('type') === 'recurring' ? 'recurring' : 'standard';
  
  const achEnabled = companySettings ? isAchPaymentAvailable(companySettings) : false;

  return {
    customerId: '',
    invoiceNumber: companySettings ? generateInvoiceNumber(companySettings) : '',
    poNumber: '',
    date: today,
    dueDate: today,
    paymentTerms: 'due_on_receipt',
    paymentMethods: {
      credit_card: true,
      paypal: companySettings?.invoiceSettings?.paymentMethods?.paypal || false,
      ach: (companySettings?.invoiceSettings?.paymentMethods?.ach || false) && achEnabled,
      check: companySettings?.invoiceSettings?.paymentMethods?.check || false
    },
    autoPayEnabled: true,  // Always true by default
    items: [],
    notes: companySettings?.invoiceSettings?.defaultNotes || '',
    footerText: companySettings?.invoiceSettings?.customFooterText || '',
    invoiceType: typeFromUrl,
    nextGenerationDate: null,
    recurringDetails: null,
    scheduledSendDate: null
  };
};

// Defensive date formatting helper
const safeFormatDate = (date: string | Date | null | undefined) => {
  if (!date) return '';
  if (typeof date === 'string') return date; // Assume already in 'YYYY-MM-DD'
  // If it's a Date object, format as local date
  return getLocalDateString(date);
};

// Add a helper function for date validation and formatting
const formatDateForDB = (dateStr: string): string => {
  try {
    if (!dateStr) return getLocalDateString(new Date());
    
    // For dates from input[type="date"], ensure proper local interpretation
    // Parse the parts directly to avoid any timezone issues
    const [year, month, day] = dateStr.split('-').map(Number);
    if (isNaN(year) || isNaN(month) || isNaN(day)) {
      throw new Error('Invalid date format');
    }
    
    // Create date object with the local date parts
    // month-1 because JavaScript months are 0-indexed
    const date = new Date(year, month - 1, day, 12, 0, 0);
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  } catch (error) {
    console.error('Error formatting date:', error, dateStr);
    // Return today's date as fallback
    return getLocalDateString(new Date());
  }
};

// Add this interface at the top of the file with other interfaces
interface InvoiceWithMetadata extends Invoice {
  metadata?: {
    isRecurring?: boolean;
    isInstallment?: boolean;
    wasRefunded?: boolean;
    refundDate?: string;
    statusOverride?: string;
    [key: string]: any;
  };
}

export default function NewInvoice() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isRecurring = searchParams.get('type') === 'recurring';
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const companyId = user?.companyId || '';
  const [isSaved, setIsSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [savedInvoiceData, setSavedInvoiceData] = useState<Invoice | null>(null);
  
  // Check for step parameter in URL
  const initialStep = searchParams.get('step') ? parseInt(searchParams.get('step') || '1') : 1;
  const [currentStep, setCurrentStep] = useState<number>(initialStep);

  const [frequencyDetails, setFrequencyDetails] = useState<RecurringFrequencyDetails>({
    type: '', // Changed from 'monthly' to empty string for "Please select"
    every: 1,
    dayOfMonth: 1,
    duePreference: 'first_of_month',
    invoiceIssueDaysBeforeDue: 0
  });

  // Get the current pathname to check if we're in template edit mode
  const pathname = window.location.pathname;
  const isEditingTemplate = pathname.includes('/invoices/edit-template/');
  
  // Add global styles to remove number input arrows
  useEffect(() => {
    // Create a style element
    const style = document.createElement('style');
    style.innerHTML = `
      /* Remove arrows from number inputs */
      input[type=number]::-webkit-inner-spin-button, 
      input[type=number]::-webkit-outer-spin-button { 
        -webkit-appearance: none; 
        margin: 0; 
      }
      input[type=number] {
        -moz-appearance: textfield;
      }
    `;
    // Append the style to the document head
    document.head.appendChild(style);
    
    // Clean up function to remove the style element when component unmounts
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const [isAddCustomerDialogOpen, setIsAddCustomerDialogOpen] = useState(false);
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [isCustomerSearchFocused, setIsCustomerSearchFocused] = useState(false);
  const [filteredCustomers, setFilteredCustomers] = useState<Customer[]>([]);

  // Product/Category search states for line items
  const [lineItemSearchStates, setLineItemSearchStates] = useState<{
    [key: string]: {
      categorySearch: string;
      productSearch: string;
      isCategoryFocused: boolean;
      isProductFocused: boolean;
      filteredCategories: any[];
      filteredProducts: any[];
    }
  }>({});
  const [isAddProductDialogOpen, setIsAddProductDialogOpen] = useState(false);
  const [isAddCategoryDialogOpen, setIsAddCategoryDialogOpen] = useState(false);
  const [preselectedCategoryForProduct, setPreselectedCategoryForProduct] = useState<string | undefined>(undefined);
  const [currentLineItemIndex, setCurrentLineItemIndex] = useState<number | null>(null);

  // Update the initial state
  const [formData, setFormData] = useState<InvoiceFormData>(() => {
    // Ensure we get today's date properly in local timezone
    const today = getLocalDateString(new Date());
    return {
      customerId: '',
      invoiceNumber: '',
      poNumber: '',
      date: today,
      dueDate: today,
      paymentTerms: 'due_on_receipt',
      paymentMethods: {
        credit_card: true,
        paypal: false,
        ach: false,
        check: false
      },
      autoPayEnabled: true, // Always true by default
      items: [],
      notes: '',
      footerText: '',
      invoiceType: searchParams.get('type') === 'recurring' ? 'recurring' : 'standard',
      nextGenerationDate: null,
      recurringDetails: null,
      scheduledSendDate: null
    };
  });

  // Fetch data
  const { data: customers = [] } = useQuery({
    queryKey: ['customers', user?.companyId],
    queryFn: () => getCustomers(user?.companyId),
    enabled: !!user?.companyId
  });

  const { data: categories = [] } = useQuery({
    queryKey: ['categories', user?.companyId],
    queryFn: async ({ queryKey }) => {
      const [_, companyId] = queryKey;
      return getCategories(companyId as string);
    },
    enabled: !!user?.companyId
  });

  const { data: products = [] } = useQuery({
    queryKey: ['products', user?.companyId],
    queryFn: async ({ queryKey }) => {
      const [_, companyId] = queryKey;
      return getProducts(companyId as string);
    },
    enabled: !!user?.companyId
  });

  // Add query for company
  const { data: company } = useQuery({
    queryKey: ['company', user?.companyId],
    queryFn: () => user?.companyId ? getCompany(user.companyId) : null,
    enabled: !!user?.companyId
  });

  const achAvailable = useMemo(() => isAchPaymentAvailable(company), [company]);

  // Add query for existing invoices to check duplicates
  const { data: existingInvoices = [] } = useQuery({
    queryKey: ['invoices', user?.companyId],
    queryFn: () => getInvoices(user?.companyId),
    enabled: !!user?.companyId
  });

  // Add state to track if we're editing
  const isEditing = useMemo(() => {
    return id ? { isEdit: true, invoiceId: id } : { isEdit: false, invoiceId: null };
  }, [id]);

  // Add query to fetch specific invoice when editing
  const { data: editingInvoice, isLoading: isLoadingInvoice } = useQuery({
    queryKey: ['invoice', id],
    queryFn: () => getInvoice(id as string),
    enabled: !!id,
  });

  // Add this new query:
  // Add query to fetch template when template ID is provided
  const { data: templateData, isLoading: isLoadingTemplate } = useQuery({
    queryKey: ['template', searchParams.get('template')],
    queryFn: async () => {
      if (!searchParams.get('template')) return null;
      
      try {
        const templateRef = doc(db, 'invoiceTemplates', searchParams.get('template'));
        const templateSnap = await getDoc(templateRef);
        
        if (templateSnap.exists()) {
          const data = templateSnap.data();
          return { id: templateSnap.id, ...data };
        }
        
        return null;
      } catch (err) {
        console.error('Error fetching template:', err);
        return null;
      }
    },
    enabled: !!searchParams.get('template'),
  });
  
  // Add query to fetch template when in edit-template mode
  const { data: editingTemplateData, isLoading: isLoadingEditingTemplate } = useQuery({
    queryKey: ['editTemplate', id, isEditingTemplate],
    queryFn: async () => {
      if (!id || !isEditingTemplate) return null;
      
      try {
        const templateRef = doc(db, 'invoiceTemplates', id);
        const templateSnap = await getDoc(templateRef);
        
        if (templateSnap.exists()) {
          const data = templateSnap.data();
          return { id: templateSnap.id, ...data };
        }
        
        return null;
      } catch (err) {
        console.error('Error fetching template for direct editing:', err);
        return null;
      }
    },
    enabled: !!id && isEditingTemplate,
  });

  // Redirect if trying to edit a paid invoice
  useEffect(() => {
    if (editingInvoice && editingInvoice.status === 'paid') {
      showNotification({
        title: "Cannot Edit Paid Invoice",
        message: "Paid invoices cannot be modified. You can only view them.",
        type: "warning"
      });
      navigate('/app/invoices');
    }
  }, [editingInvoice, navigate]);

  // Effect to update form data when editing invoice is loaded
  useEffect(() => {
    if (editingInvoice && id) {
      
      // Format dates to YYYY-MM-DD for input[type="date"]
      const formatDate = (date: string | Date) => {
        if (!date) return '';
        if (typeof date === 'string') return date;
        return getLocalDateString(date);
      };

      // Calculate payment terms based on due date
      const calculatePaymentTerms = (date: string, dueDate: string): InvoiceFormData['paymentTerms'] => {
        const days = Math.round((new Date(dueDate).getTime() - new Date(date).getTime()) / (1000 * 60 * 60 * 24));
        if (days <= 0) return 'due_on_receipt';
        if (days <= 15) return 'net_15';
        if (days <= 30) return 'net_30';
        return 'net_60';
      };

      setFormData({
        customerId: editingInvoice.customerId,
        invoiceNumber: editingInvoice.invoiceNumber,
        poNumber: '',
        date: editingInvoice.date,
        dueDate: editingInvoice.dueDate,
        paymentTerms: calculatePaymentTerms(editingInvoice.date, editingInvoice.dueDate),
        paymentMethods: {
          credit_card: editingInvoice.paymentMethods.includes('credit_card'),
          paypal: editingInvoice.paymentMethods.includes('paypal'),
          ach: editingInvoice.paymentMethods.includes('ach') && achAvailable,
          check: editingInvoice.paymentMethods.includes('check'),
        },
        autoPayEnabled: editingInvoice.autoPayEnabled ?? true, // Default to true if undefined
        items: editingInvoice.items || [],
        notes: editingInvoice.notes || '',
        footerText: editingInvoice.footerText || '',
        invoiceType: editingInvoice.invoiceType || 'standard',
        nextGenerationDate: editingInvoice.nextGenerationDate ? new Date(editingInvoice.nextGenerationDate) : null,
        recurringDetails: editingInvoice.recurringDetails ? {
          ...editingInvoice.recurringDetails,
          endDate: editingInvoice.recurringDetails.endDate ? new Date(editingInvoice.recurringDetails.endDate) : null,
        } : null,
      });
      // Set customerSearchTerm to display name
      const customer = customers.find(c => c.id === editingInvoice.customerId);
      setCustomerSearchTerm(
        customer?.companyName || (customer ? `${customer.firstName} ${customer.lastName}` : '')
      );
    }
  }, [editingInvoice, id, customers, achAvailable]);

  // Add this effect after the editingInvoice effect
  // Effect to update form data when template data is loaded
  useEffect(() => {
    if (templateData && searchParams.get('template')) {
      
      // Use type assertion to handle the template data
      const template = templateData as any;
      
      // Format dates to YYYY-MM-DD for input[type="date"]
      const formatDate = (date: string | Date) => {
        if (!date) return '';
        if (typeof date === 'string') return date;
        return getLocalDateString(date);
      };
      
      // Create a valid form data object from template data
      const newFormData: InvoiceFormData = {
        customerId: template.customerDetails?.id || '',
        invoiceNumber: template.invoiceNumber || '',
        poNumber: '', // Templates usually don't have PO numbers
        date: formatDate(new Date()), // Use current date for new invoice
        dueDate: formatDate(new Date(new Date().setDate(new Date().getDate() + 30))), // Default to 30 days
        paymentTerms: 'due_on_receipt',
        paymentMethods: {
          credit_card: template.paymentMethods?.includes('credit_card') || false,
          paypal: template.paymentMethods?.includes('paypal') || false,
          ach: (template.paymentMethods?.includes('ach') || template.recurringDetails?.enableACH || false) && achAvailable,
          check: template.paymentMethods?.includes('check') || false
        },
        autoPayEnabled: template.autoPayEnabled ?? true, // Default to true if undefined
        items: template.lineItems || [],
        notes: template.invoiceDetails?.notes || '',
        footerText: template.footerText || '',
        invoiceType: 'recurring', // Templates are always recurring
        nextGenerationDate: template.nextGenerationDate ? new Date(template.nextGenerationDate) : null,
        recurringDetails: {
          frequency: template.recurringDetails?.frequency || 'monthly',
          isActive: template.recurringDetails?.isActive ?? true,
          frequencyDetails: template.recurringDetails?.frequencyDetails || frequencyDetails
        }
      };
      
      // Update form data with template values
      setFormData(newFormData);
      
      // Find the customer for properly displaying company name or full name
      const customerId = template.customerDetails?.id || '';
      const customer = customers.find(c => c.id === customerId);
      
      // Set customer display name - first try company name, then fall back to full name
      if (customer) {
        setCustomerSearchTerm(
          customer.companyName || `${customer.firstName} ${customer.lastName}`
        );
      } else if (template.customerName) {
        // Fallback to template's customer name if customer not found in list
        setCustomerSearchTerm(template.customerName);
      }
      
      // Set form as dirty since we loaded data
      setIsDirty(true);
    }
  }, [templateData, searchParams, customers, frequencyDetails, achAvailable]);

  // Add this effect after the editingInvoice effect and templateData effect
  useEffect(() => {
    if (editingTemplateData && isEditingTemplate && id) {

      // Check if the template has been activated
      if ((editingTemplateData as any).hasBeenActivated) {
        showNotification({
          title: "Template Locked",
          message: "This template has been activated and can no longer be edited. You are being redirected to view its details.",
          type: "warning" // Changed from "info" to "warning"
        });
        navigate(`/app/invoices/preview?id=${id}&viewOnly=true`); // Added viewOnly param
        return; // Stop further processing to prevent form population
      }
      
      // Use type assertion to handle the template data
      const template = editingTemplateData as any;
      
      // Format dates to YYYY-MM-DD for input[type="date"]
      const formatDate = (date: string | Date) => {
        if (!date) return '';
        if (typeof date === 'string') return date;
        return getLocalDateString(date);
      };
      
      // Create a valid form data object from template data
      const newFormData: InvoiceFormData = {
        customerId: template.customerDetails?.id || '',
        invoiceNumber: template.invoiceNumber || '',
        poNumber: '', // Templates usually don't have PO numbers
        date: formatDate(new Date()), // Use current date for new invoice
        dueDate: formatDate(new Date(new Date().setDate(new Date().getDate() + 30))), // Default to 30 days
        paymentTerms: 'due_on_receipt',
        paymentMethods: {
          credit_card: template.paymentMethods?.includes('credit_card') || false,
          paypal: template.paymentMethods?.includes('paypal') || false,
          ach: (template.paymentMethods?.includes('ach') || template.recurringDetails?.enableACH || false) && achAvailable,
          check: template.paymentMethods?.includes('check') || false
        },
        autoPayEnabled: template.autoPayEnabled ?? true, // Default to true if undefined
        items: template.lineItems || [],
        notes: template.invoiceDetails?.notes || '',
        footerText: template.footerText || '',
        invoiceType: 'recurring', // Templates are always recurring
        nextGenerationDate: template.nextGenerationDate ? new Date(template.nextGenerationDate) : null,
        recurringDetails: {
          frequency: template.recurringDetails?.frequency || 'monthly',
          isActive: template.recurringDetails?.isActive ?? true,
          frequencyDetails: template.recurringDetails?.frequencyDetails || frequencyDetails
        }
      };
      
      // Update form data with template values
      setFormData(newFormData);
      
      // Find the customer for properly displaying company name or full name
      const customerId = template.customerDetails?.id || '';
      const customer = customers.find(c => c.id === customerId);
      
      // Set customer display name - first try company name, then fall back to full name
      if (customer) {
        setCustomerSearchTerm(
          customer.companyName || `${customer.firstName} ${customer.lastName}`
        );
      } else if (template.customerName) {
        // Fallback to template's customer name if customer not found in list
        setCustomerSearchTerm(template.customerName);
      }
      
      // Set form as dirty since we loaded data
      setIsDirty(true);
      
      // Update isEditing state to ensure we update the template rather than creating a new one
      isEditing.isEdit = true;
      isEditing.invoiceId = id;
      
      // Force invoiceType to be recurring since it's a template
      setFormData(prev => ({
        ...prev,
        invoiceType: 'recurring'
      }));
    }
  }, [editingTemplateData, isEditingTemplate, id, frequencyDetails, customers, achAvailable]);

  // Create invoice mutation
  const createInvoiceMutation = useMutation({
    mutationFn: createInvoice,
    onSuccess: (savedInvoice) => {
      queryClient.invalidateQueries({ queryKey: ['invoices', user?.companyId] });
      // Update the form with the saved invoice ID to make it an edit mode
      setFormData(prev => ({
        ...prev,
        id: savedInvoice.id
      }));
      // Update editing state to reflect we're now editing an existing invoice
      isEditing.isEdit = true;
      isEditing.invoiceId = savedInvoice.id;
    }
  });

  // Add update invoice mutation
  const updateInvoiceMutation = useMutation({
    mutationFn: ({ id, invoice }: { id: string; invoice: Partial<Invoice> }) => 
      updateInvoice(id, invoice),
    onSuccess: (savedInvoice: Invoice) => {
      queryClient.invalidateQueries({ queryKey: ['invoices', user?.companyId] });
      showNotification({
        title: "Success",
        message: "Invoice updated successfully",
        type: "success"
      });
      // Keep savedInvoice for future use
    }
  });

  // Handle payment terms change
  const handlePaymentTermsChange = (terms: string) => {
    updateFormData(prev => ({
      ...prev,
      paymentTerms: terms as PaymentTerms,
      dueDate: calculateDueDate(prev.date, terms)
    }));
  };

  // Handle adding new line item
  const addLineItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [
        ...prev.items,
        {
          id: Date.now().toString(),
          categoryName: '',
          productName: '',
          description: '',
          quantity: 1,
          unitPrice: 0,
          total: 0,
          isTaxable: false
        }
      ]
    }));
  };

  // Update setFormData to mark form as dirty
  const updateFormData = (newData: InvoiceFormData | ((prev: InvoiceFormData) => InvoiceFormData)) => {
    setFormData(newData);
    setIsDirty(true);
    setIsSaved(false);
  };

  // Update handleLineItemChange to handle manual entries
  const handleLineItemChange = (index: number, field: keyof LineItem, value: LineItem[keyof LineItem]) => {
    updateFormData(prev => {
      const updatedItems = [...prev.items];
      const currentItem = { ...updatedItems[index] };

      if (field === 'isTaxable') {
        currentItem.isTaxable = value as boolean;
      } else if (field === 'categoryName' && typeof value === 'string') {
        currentItem.categoryName = value;
        // Only set categoryId if it exists in the database
        const category = categories.find(c => c.name === value);
        currentItem.categoryId = category?.id;
      } else if (field === 'productName' && typeof value === 'string') {
        currentItem.productName = value;
        // Only set productId if it exists in the database
        const product = products.find(p => p.name === value);
        if (product) {
          currentItem.productId = product.id;
          currentItem.description = product.description;
          currentItem.unitPrice = product.price;
          currentItem.isTaxable = product.isTaxable;
          currentItem.total = product.price * currentItem.quantity;
        } else {
          // For manual entries, use the product name as description if no description exists
          if (!currentItem.description) {
            currentItem.description = value;
          }
          currentItem.productId = undefined;
        }
      } else if (field === 'description' && typeof value === 'string') {
        currentItem.description = value;
      } else if (field === 'quantity') {
        currentItem.quantity = value as number;
        currentItem.total = currentItem.quantity * currentItem.unitPrice;
      } else if (field === 'unitPrice') {
        currentItem.unitPrice = value as number;
        currentItem.total = currentItem.quantity * currentItem.unitPrice;
      }

      updatedItems[index] = currentItem;
      return { ...prev, items: updatedItems };
    });
  };

  const handleCategorySelect = (index: number, value: string) => {
    const item = formData.items[index];

    if (value === CLEAR_SELECT_VALUE) {
      updateFormData(prev => {
        const updatedItems = [...prev.items];
        const currentItem = { ...updatedItems[index] };
        currentItem.categoryId = undefined;
        currentItem.categoryName = '';
        currentItem.productId = undefined;
        currentItem.productName = '';
        currentItem.description = '';
        currentItem.unitPrice = 0;
        currentItem.total = 0;
        currentItem.isTaxable = false;
        updatedItems[index] = currentItem;
        return { ...prev, items: updatedItems };
      });

      // Update search states
      setLineItemSearchStates(prev => ({
        ...prev,
        [item.id]: {
          ...prev[item.id],
          categorySearch: '',
          productSearch: '',
          filteredProducts: products
        }
      }));
      return;
    }

    const selectedCategory = categories.find(cat => cat.id === value);
    if (!selectedCategory) return;

    updateFormData(prev => {
      const updatedItems = [...prev.items];
      const currentItem = { ...updatedItems[index] };
      currentItem.categoryId = selectedCategory.id;
      currentItem.categoryName = selectedCategory.name;
      currentItem.productId = undefined;
      currentItem.productName = '';
      currentItem.description = '';
      currentItem.unitPrice = 0;
      currentItem.total = 0;
      currentItem.isTaxable = false;
      updatedItems[index] = currentItem;
      return { ...prev, items: updatedItems };
    });

    // Update search states
    setLineItemSearchStates(prev => ({
      ...prev,
      [item.id]: {
        ...prev[item.id],
        categorySearch: selectedCategory.name,
        productSearch: '',
        isCategoryFocused: false,
        filteredProducts: products.filter(product => product.category === selectedCategory.id)
      }
    }));
  };

  const handleProductSelect = (index: number, value: string) => {
    const item = formData.items[index];

    if (value === CLEAR_SELECT_VALUE) {
      updateFormData(prev => {
        const updatedItems = [...prev.items];
        const currentItem = { ...updatedItems[index] };
        currentItem.productId = undefined;
        currentItem.productName = '';
        currentItem.description = '';
        currentItem.unitPrice = 0;
        currentItem.total = currentItem.quantity * currentItem.unitPrice;
        currentItem.isTaxable = false;
        updatedItems[index] = currentItem;
        return { ...prev, items: updatedItems };
      });

      // Update search states
      setLineItemSearchStates(prev => ({
        ...prev,
        [item.id]: {
          ...prev[item.id],
          productSearch: '',
          isProductFocused: false
        }
      }));
      return;
    }

    const selectedProduct = products.find(prod => prod.id === value);
    if (!selectedProduct) return;

    updateFormData(prev => {
      const updatedItems = [...prev.items];
      const currentItem = { ...updatedItems[index] };
      currentItem.productId = selectedProduct.id;
      currentItem.productName = selectedProduct.name;
      currentItem.description = selectedProduct.description || '';
      currentItem.unitPrice = selectedProduct.price || 0;
      const quantity = currentItem.quantity > 0 ? currentItem.quantity : 1;
      currentItem.quantity = quantity;
      currentItem.total = quantity * currentItem.unitPrice;
      currentItem.isTaxable = selectedProduct.isTaxable ?? currentItem.isTaxable;
      updatedItems[index] = currentItem;
      return { ...prev, items: updatedItems };
    });

    // Update search states
    setLineItemSearchStates(prev => ({
      ...prev,
      [item.id]: {
        ...prev[item.id],
        productSearch: selectedProduct.name,
        isProductFocused: false
      }
    }));
  };

  // Handle removing line item
  const removeLineItem = (index: number) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  // Calculate totals
  const calculateTotals = () => {
    const taxRate = (company?.defaultTaxRate ?? 0) / 100;
    const subtotal = formData.items.reduce((acc, item) => acc + item.total, 0);
    const taxableAmount = formData.items.reduce((acc, item) => 
      acc + (item.isTaxable ? item.total : 0), 0
    );
    const taxAmount = taxableAmount * taxRate;

    let total = subtotal + taxAmount;

    // Add platform fee if enabled
    if (company?.invoiceSettings?.enablePlatformFee && company.platformFee) {
      // Apply platform fee to subtotal + tax instead of just subtotal
      const platformFeeAmount = (subtotal + taxAmount) * (company.platformFee / 100);
      total += platformFeeAmount;
    }

    return { subtotal, taxAmount, total };
  };

  // Add function to record an activity when saving
  const recordInvoiceActivity = (type: InvoiceActivity['type'], description: string, metadata?: Record<string, any>): InvoiceActivity => {
    const activity: InvoiceActivity = {
      id: uuidv4(),
      type,
      description,
      timestamp: getLocalDateString(new Date()), // Correct timestamp format
      performedBy: user?.email || 'system', // Correct field for user tracking
      // metadata is initially undefined here by object construction
    };

    if (metadata) {
      activity.metadata = metadata; // Set if provided
    } else {
      activity.metadata = null; // Explicitly set to null if not provided
    }

    return activity;
  };

  // Enhanced function to remove undefined values and validate objects deeply
  const removeUndefinedValues = (obj: any): any => {
    if (obj === null) return null;
    if (obj === undefined) return null;
    if (typeof obj !== 'object') return obj;
    
    // Handle arrays specially
    if (Array.isArray(obj)) {
      return obj.map(item => removeUndefinedValues(item));
    }
    
    const cleanObj: any = {};
    
    Object.entries(obj).forEach(([key, value]) => {
      // Skip keys with undefined values completely
      if (value === undefined) {
        cleanObj[key] = null;
      } else if (value === null) {
        cleanObj[key] = null;
      } else if (typeof value === 'object') {
        cleanObj[key] = removeUndefinedValues(value);
      } else {
        cleanObj[key] = value;
      }
    });
    
    return cleanObj;
  };

  // Deep object validation to find any undefined values
  const findUndefinedValues = (obj: any, path = ''): string[] => {
    const results: string[] = [];
    
    if (obj === undefined) {
      results.push(path || 'root');
      return results;
    }
    
    if (obj === null || typeof obj !== 'object') {
      return results;
    }
    
    if (Array.isArray(obj)) {
      obj.forEach((item, index) => {
        const itemPath = path ? `${path}[${index}]` : `[${index}]`;
        results.push(...findUndefinedValues(item, itemPath));
      });
      return results;
    }
    
    Object.entries(obj).forEach(([key, value]) => {
      const currentPath = path ? `${path}.${key}` : key;
      
      if (value === undefined) {
        results.push(currentPath);
      } else if (value !== null && typeof value === 'object') {
        results.push(...findUndefinedValues(value, currentPath));
      }
    });
    
    return results;
  };

  // Update handleSaveInvoice to handle recurring details more carefully
  const handleSaveInvoice = async (sendEmail: boolean = false, callback?: () => void, preview: boolean = false, overrideNextGenerationDate?: string | null, saveAsDraft: boolean = false) => {
    try {
      setSaving(true);
      
      // Calculate totals for the invoice
      const { subtotal, taxAmount, total } = calculateTotals();
      
      // Generate a payment key for online payments
      const paymentKey = `pay_${uuidv4().replace(/-/g, '')}`;
      
      // Find the selected customer from the customers list
      const selectedCustomer = customers.find(c => c.id === formData.customerId);
      
      // Make a deep copy of the form data to avoid reference issues
      const formDataCopy = JSON.parse(JSON.stringify(formData));
      
      // Determine the correct status based on the action
      let invoiceStatus: 'draft' | 'pending' | 'sent' = 'pending';
      if (saveAsDraft) {
        invoiceStatus = 'draft';
      } else if (sendEmail) {
        invoiceStatus = 'sent';
      }
      
      // Prepare invoice data for saving
      let invoiceData: Partial<Invoice> = {
        companyId: user?.companyId || '',
        customerId: formDataCopy.customerId,
        customerName: selectedCustomer?.firstName && selectedCustomer?.lastName 
          ? `${selectedCustomer.firstName} ${selectedCustomer.lastName}`
          : 'Unknown Customer',
        customerCompanyName: selectedCustomer?.companyName || '',
        customerAddress: `${selectedCustomer?.street || ''}, ${selectedCustomer?.city || ''}, ${selectedCustomer?.state || ''} ${selectedCustomer?.zip || ''}`.replace(/^, |, ,/g, '').trim(),
        customerEmail: selectedCustomer?.email || '',
        invoiceNumber: formDataCopy.invoiceNumber,
        date: formatDateForDB(formDataCopy.date), // Ensure proper date format
        dueDate: formatDateForDB(formDataCopy.dueDate), // Ensure proper date format
        subtotal: subtotal || 0,
        taxRate: (company?.defaultTaxRate ?? 0) / 100,
        taxAmount: taxAmount || 0,
        platformFee: 0, // Set default value to prevent undefined
        total: total || 0,
        status: invoiceStatus,
        invoiceType: formDataCopy.invoiceType || 'standard', // Ensure invoice type is set
        autoPayEnabled: formDataCopy.autoPayEnabled === true,
        items: formDataCopy.items.map(item => ({
          productName: item.productName || '',
          description: item.description || '',
          quantity: item.quantity || 0,
          unitPrice: item.unitPrice || 0,
          total: item.total || 0,
          isTaxable: !!item.isTaxable,
          categoryName: item.categoryName || ''
        })),
        notes: formDataCopy.notes || '',
        footerText: formDataCopy.footerText || '',
        paymentMethods: Object.entries(formDataCopy.paymentMethods)
          .filter(([_, enabled]) => enabled)
          .map(([method]) => method as 'credit_card' | 'paypal' | 'ach' | 'check'),
        paymentKey,
        scheduledSendDate: formDataCopy.scheduledSendDate ? formDataCopy.scheduledSendDate : null
      };
      
      
      // Handle recurring details with special care
      if (formDataCopy.invoiceType === 'recurring') {
        
        // Determine the next generation date
        const ndg = overrideNextGenerationDate !== undefined 
          ? new Date(overrideNextGenerationDate) 
          : formDataCopy.nextGenerationDate ? new Date(formDataCopy.nextGenerationDate) : null;

        // Create basic recurring details
        const basicRecurringDetails = {
          frequency: formDataCopy.recurringDetails?.frequency === 'every_minute' 
            ? 'monthly' 
            : (formDataCopy.recurringDetails?.frequency as 'weekly' | 'monthly' | 'quarterly' | 'annual') || 'monthly',
          endDate: formDataCopy.recurringDetails?.endDate || null,
          remainingOccurrences: formDataCopy.recurringDetails?.remainingOccurrences || null,
          isActive: true,
          frequencyDetails: formDataCopy.recurringDetails?.frequencyDetails || null,
          enableACH: formDataCopy.recurringDetails?.enableACH || false
        };
        
        // Add recurring details to invoice data
        invoiceData.invoiceType = 'recurring';
        invoiceData.nextGenerationDate = ndg;
        invoiceData.recurringDetails = basicRecurringDetails;
        
        
        // If using our new frequency details system
        if (formDataCopy.recurringDetails?.frequencyDetails) {
          
          // Create a clean frequency details object with only the necessary fields
          const frequencyType = formDataCopy.recurringDetails.frequencyDetails.type || 'monthly';
          
          const cleanFrequencyDetails = {
            type: frequencyType,
            every: formDataCopy.recurringDetails.frequencyDetails.every || 1,
            duePreference: formDataCopy.recurringDetails.frequencyDetails.duePreference || 'anniversary',
            invoiceIssueDaysBeforeDue: formDataCopy.recurringDetails.frequencyDetails.invoiceIssueDaysBeforeDue ?? 0
          };
          
          // Add type-specific properties based on frequency type
          if (frequencyType === 'weekly') {
            cleanFrequencyDetails['dayOfWeek'] = formDataCopy.recurringDetails.frequencyDetails.dayOfWeek || 'monday';
          }
          
          if (frequencyType === 'monthly' || frequencyType === 'yearly') {
            // For dayOfMonth, ensure it's either a number or one of the allowed string values
            const dayOfMonth = formDataCopy.recurringDetails.frequencyDetails.dayOfMonth;
            if (typeof dayOfMonth === 'number') {
              cleanFrequencyDetails['dayOfMonth'] = dayOfMonth;
            } else if (dayOfMonth === 'first' || dayOfMonth === '15th' || dayOfMonth === 'last') {
              cleanFrequencyDetails['dayOfMonth'] = dayOfMonth;
            } else {
              cleanFrequencyDetails['dayOfMonth'] = 1;
            }
          }
          
          
          // Add the cleaned frequency details to the recurring details
          basicRecurringDetails['frequencyDetails'] = cleanFrequencyDetails;
        }
        
        // Assign the fully prepared recurring details to invoice data
        invoiceData.recurringDetails = basicRecurringDetails;
      }
      
      // Convert to raw object and back to strip any remaining undefined values
      const cleanedJson = JSON.stringify(invoiceData);
      invoiceData = JSON.parse(cleanedJson);
      
      
      let savedInvoiceData: Invoice | null = null;
      
      // Special handling for template editing
      if (isEditingTemplate && id) {
        
        // Create template data from invoice data
        const templateData: any = {
          ...invoiceData,
          customerDetails: {
            id: invoiceData.customerId,
            name: invoiceData.customerName,
            email: invoiceData.customerEmail,
            address: invoiceData.customerAddress
          },
          invoiceDetails: {
            subtotal: invoiceData.subtotal,
            taxRate: invoiceData.taxRate,
            taxAmount: invoiceData.taxAmount,
            platformFee: invoiceData.platformFee,
            total: invoiceData.total,
            notes: invoiceData.notes || ''
          },
          lineItems: invoiceData.items,
          status: 'inactive',
          schedulePreview: {
            frequency: `Every ${frequencyDetails.every} ${frequencyDetails.type}${frequencyDetails.type === 'weekly' ? `(s) on ${frequencyDetails.dayOfWeek}` : ''}${
              frequencyDetails.type === 'monthly' ? 
                frequencyDetails.duePreference === 'first_of_month' ? ' on the 1st' :
                frequencyDetails.duePreference === '15th_of_month' ? ' on the 15th' :
                frequencyDetails.duePreference === 'specific_date' ? ` on the ${frequencyDetails.dayOfMonth}${frequencyDetails.dayOfMonth === 1 ? 'st' : frequencyDetails.dayOfMonth === 2 ? 'nd' : frequencyDetails.dayOfMonth === 3 ? 'rd' : 'th'}` :
                ' on the monthly anniversary'
              : ''
            }`,
            firstInvoiceDate: calculateInvoiceIssueDate(formData.dueDate, frequencyDetails.invoiceIssueDaysBeforeDue),
            firstPaymentDue: formatDateForDB(formData.dueDate),
            secondInvoiceDate: calculateInvoiceIssueDate(calculateNextDueDate(formData.dueDate, frequencyDetails), frequencyDetails.invoiceIssueDaysBeforeDue),
            secondPaymentDue: calculateNextDueDate(formData.dueDate, frequencyDetails),
            invoiceGenerationDays: frequencyDetails.invoiceIssueDaysBeforeDue
          },
          // Ensure hasBeenActivated is set if recurringDetails indicate it's active
          ...(invoiceData.recurringDetails?.isActive && { hasBeenActivated: true }),
          updatedAt: serverTimestamp()
        };
        
        // Update the template in Firestore
        const templateRef = doc(db, 'invoiceTemplates', id);
        await updateDoc(templateRef, templateData);
        
        // Update the local state to reflect that we're now editing the saved template
        setIsSaved(true);
        
        // Show success notification
        showNotification({
          title: 'Success',
          message: 'Template updated successfully',
          type: 'success'
        });
        
        
        // Navigate back to the templates list if not in preview mode
        if (!preview) {
          navigate('/app/invoices?tab=recurring');
        }
      }
      // Create a new invoice or update existing one
      else if (isEditing.isEdit && isEditing.invoiceId) {
        // Record invoice update activity
        const updateActivity = recordInvoiceActivity(
          'edited',
          'Invoice updated',
          { previousTotal: editingInvoice?.total }
        );
        
        // Update existing invoice
        savedInvoiceData = await updateInvoiceMutation.mutateAsync({
          id: isEditing.invoiceId,
          invoice: {
            ...invoiceData,
            // Append the new activity to the existing activities
            activities: [
              ...(editingInvoice?.activities || []),
              updateActivity
            ]
          }
        });
        
        console.log('Invoice updated successfully:', savedInvoiceData.id);
      } else {
        // Record invoice creation activity
        const creationActivity = recordInvoiceActivity(
          'pending',
          'Invoice created with pending status'
        );
        
        // Ensure companyId is always set (required by the Invoice type)
        const companyId = user?.companyId;
        if (!companyId) {
          throw new Error('Company ID is required but not available');
        }
        
        // Ensure all required fields for Invoice type are present
        const finalInvoiceData = {
          ...invoiceData,
          companyId,
          invoiceNumber: invoiceData.invoiceNumber || `INV-${Date.now()}`,
          customerId: invoiceData.customerId || '',
          customerName: invoiceData.customerName || 'Unknown Customer',
                      date: formatDateForDB(formDataCopy.date), // Use the date from the form, ensure proper format
          dueDate: formatDateForDB(formDataCopy.dueDate || formDataCopy.date), // Use due date from form or fall back to invoice date, ensure proper format
          subtotal: invoiceData.subtotal || 0,
          taxAmount: invoiceData.taxAmount || 0,
          total: invoiceData.total || 0,
          status: invoiceData.status || 'pending',
          items: invoiceData.items || [],
          paymentMethods: invoiceData.paymentMethods || [],
          autoPayEnabled: invoiceData.autoPayEnabled === true,
          activities: [creationActivity],
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now()
        };
        
        
        try {
          // Use the direct API function instead of the mutation to bypass TypeScript checks
          // @ts-expect-error - We're intentionally bypassing TypeScript here to ensure Firebase gets valid data
          const result = await createInvoice(finalInvoiceData);
          savedInvoiceData = result as Invoice;
        } catch (innerError) {
          console.error('Detailed error creating invoice:', innerError);
          
          // Log more details about the error
          if (innerError instanceof Error) {
            console.error('Error name:', innerError.name);
            console.error('Error message:', innerError.message);
            console.error('Error stack:', innerError.stack);
            
            // Check for Firebase-specific errors
            if (innerError.message.includes('undefined')) {
              console.error('Firebase undefined value error detected. Attempting to identify the problematic field...');
              // Check each top-level field
              Object.entries(finalInvoiceData).forEach(([key, value]) => {
                if (value === undefined) {
                  console.error(`Found undefined value in field: ${key}`);
                }
                // Check for deeper undefined values in objects
                if (value && typeof value === 'object') {
                  const deepCheck = (obj: any, path: string) => {
                    Object.entries(obj).forEach(([k, v]) => {
                      const fullPath = `${path}.${k}`;
                      if (v === undefined) {
                        console.error(`Found undefined value in nested field: ${fullPath}`);
                      } else if (v && typeof v === 'object' && !Array.isArray(v)) {
                        deepCheck(v, fullPath);
                      }
                    });
                  };
                  deepCheck(value, key);
                }
              });
            }
          }
          
          // Convert the final data to a pure JSON object to remove any potential class instances
          const pureJsonData = JSON.parse(JSON.stringify(finalInvoiceData));
          // Try again with the pure JSON object
          const secondResult = await createInvoice(pureJsonData);
          savedInvoiceData = secondResult as Invoice;
        }
      }
      
      // Update the savedInvoiceData state
      setSavedInvoiceData(savedInvoiceData);
      
      // Mark the form as saved and not dirty
      setIsSaved(true);
      setIsDirty(false);
      
      // Invalidate the invoices cache to ensure the new invoice appears immediately
      if (savedInvoiceData) {
        // Await cache invalidation to ensure it completes before navigation
        await queryClient.invalidateQueries({ queryKey: ['invoices', user?.companyId] });
        
        // Also invalidate the specific invoice query for consistency
        await queryClient.invalidateQueries({ queryKey: ['invoice', savedInvoiceData.id] });
      }
      
      // After successfully saving and cache is updated, navigate to the preview page if requested
      if (preview && savedInvoiceData?.id) {
        navigate(`/app/invoices/preview?id=${savedInvoiceData.id}`);
      }
      
      // Execute the callback if provided
      callback?.();
      
      return savedInvoiceData;
    } catch (error) {
      console.error('Error saving invoice:', error);
      showNotification({
        title: "Error",
        message: error instanceof Error ? error.message : "Failed to save invoice. Please try again.",
        type: "error"
      });
      return null;
    } finally {
      setSaving(false);
    }
  };

  // Simplified customer search handler - filtering is now handled by the component
  const handleCustomerSearch = (searchTerm: string) => {
    setCustomerSearchTerm(searchTerm);
  };

  // Add this effect to initialize filtered customers
  useEffect(() => {
    setFilteredCustomers(customers);
  }, [customers]);

  // Initialize search states for line items
  const initializeLineItemSearchState = (itemId: string) => {
    if (!lineItemSearchStates[itemId]) {
      setLineItemSearchStates(prev => ({
        ...prev,
        [itemId]: {
          categorySearch: '',
          productSearch: '',
          isCategoryFocused: false,
          isProductFocused: false,
          filteredCategories: [],
          filteredProducts: []
        }
      }));
    }
  };

  // Simplified category search handler - filtering is now handled by the component
  const handleCategorySearch = (itemId: string, searchTerm: string) => {
    // Find the item index
    const itemIndex = formData.items.findIndex(item => item.id === itemId);
    if (itemIndex === -1) return;

    const item = formData.items[itemIndex];
    
    // If search term is being modified and doesn't match the current category name,
    // clear the category selection to allow free text editing
    if (searchTerm !== item.categoryName) {
      updateFormData(prev => {
        const updatedItems = [...prev.items];
        const currentItem = { ...updatedItems[itemIndex] };
        
        // Clear category selection when user starts typing
        if (currentItem.categoryId || currentItem.categoryName) {
          currentItem.categoryId = undefined;
          currentItem.categoryName = '';
          // Also clear product when category is cleared
          currentItem.productId = undefined;
          currentItem.productName = '';
          currentItem.description = '';
          currentItem.unitPrice = 0;
          currentItem.total = 0;
          currentItem.isTaxable = false;
        }
        
        updatedItems[itemIndex] = currentItem;
        return { ...prev, items: updatedItems };
      });
    }
    
    setLineItemSearchStates(prev => {
      const currentState = prev[itemId] || {
        categorySearch: '',
        productSearch: '',
        isCategoryFocused: false,
        isProductFocused: false,
        filteredCategories: [],
        filteredProducts: []
      };
      
      return {
        ...prev,
        [itemId]: {
          ...currentState,
          categorySearch: searchTerm,
          productSearch: '' // Clear product search when category changes
        }
      };
    });
  };

  // Simplified product search handler - filtering is now handled by the component
  const handleProductSearch = (itemId: string, searchTerm: string, categoryId?: string) => {
    // Find the item index
    const itemIndex = formData.items.findIndex(item => item.id === itemId);
    if (itemIndex === -1) return;

    const item = formData.items[itemIndex];
    
    // If search term is being modified and doesn't match the current product name,
    // clear the product selection to allow free text editing
    if (searchTerm !== item.productName) {
      updateFormData(prev => {
        const updatedItems = [...prev.items];
        const currentItem = { ...updatedItems[itemIndex] };
        
        // Clear product selection when user starts typing
        if (currentItem.productId || currentItem.productName) {
          currentItem.productId = undefined;
          currentItem.productName = '';
          currentItem.description = '';
          currentItem.unitPrice = 0;
          currentItem.total = 0;
          currentItem.isTaxable = false;
        }
        
        updatedItems[itemIndex] = currentItem;
        return { ...prev, items: updatedItems };
      });
    }
    
    setLineItemSearchStates(prev => {
      const currentState = prev[itemId] || {
        categorySearch: '',
        productSearch: '',
        isCategoryFocused: false,
        isProductFocused: false,
        filteredCategories: [],
        filteredProducts: []
      };
      
      return {
        ...prev,
        [itemId]: {
          ...currentState,
          productSearch: searchTerm
        }
      };
    });
  };

  // Helper function to open product dialog with preselected category
  const handleAddNewProduct = (categoryId?: string, lineItemIndex?: number) => {
    setPreselectedCategoryForProduct(categoryId);
    setCurrentLineItemIndex(lineItemIndex ?? null);
    setIsAddProductDialogOpen(true);
  };

  // Helper function to open category dialog
  const handleAddNewCategory = (lineItemIndex?: number) => {
    setCurrentLineItemIndex(lineItemIndex ?? null);
    setIsAddCategoryDialogOpen(true);
  };

  // Callback when a new product is created
  const handleProductCreated = (product: Product) => {
    if (currentLineItemIndex !== null) {
      const item = formData.items[currentLineItemIndex];

      // Directly update the line item with the new product
      updateFormData(prev => {
        const updatedItems = [...prev.items];
        const currentItem = { ...updatedItems[currentLineItemIndex] };
        currentItem.productId = product.id;
        currentItem.productName = product.name;
        currentItem.description = product.description || '';
        currentItem.unitPrice = product.price || 0;
        const quantity = currentItem.quantity > 0 ? currentItem.quantity : 1;
        currentItem.quantity = quantity;
        currentItem.total = quantity * currentItem.unitPrice;
        currentItem.isTaxable = product.isTaxable ?? currentItem.isTaxable;
        updatedItems[currentLineItemIndex] = currentItem;
        return { ...prev, items: updatedItems };
      });

      // Update search states
      setLineItemSearchStates(prev => ({
        ...prev,
        [item.id]: {
          ...prev[item.id],
          productSearch: product.name,
          isProductFocused: false
        }
      }));
    }
  };

  // Callback when a new category is created
  const handleCategoryCreated = (category: Category) => {
    if (currentLineItemIndex !== null) {
      const item = formData.items[currentLineItemIndex];

      // Directly update the line item with the new category
      updateFormData(prev => {
        const updatedItems = [...prev.items];
        const currentItem = { ...updatedItems[currentLineItemIndex] };
        currentItem.categoryId = category.id;
        currentItem.categoryName = category.name;
        // Clear product selection when category changes
        currentItem.productId = undefined;
        currentItem.productName = '';
        currentItem.description = '';
        currentItem.unitPrice = 0;
        currentItem.total = 0;
        currentItem.isTaxable = false;
        updatedItems[currentLineItemIndex] = currentItem;
        return { ...prev, items: updatedItems };
      });

      // Update search states
      setLineItemSearchStates(prev => ({
        ...prev,
        [item.id]: {
          ...prev[item.id],
          categorySearch: category.name,
          productSearch: '',
          isCategoryFocused: false,
          filteredProducts: [] // Will be updated when products query refreshes
        }
      }));
    }
  };

  // Handle custom category text input (when user types without selecting)
  const handleCategoryCustomInput = (index: number, categoryName: string) => {
    const item = formData.items[index];

    updateFormData(prev => {
      const updatedItems = [...prev.items];
      const currentItem = { ...updatedItems[index] };
      currentItem.categoryId = undefined; // No ID for custom text
      currentItem.categoryName = categoryName;
      // Clear product when category changes
      currentItem.productId = undefined;
      currentItem.productName = '';
      currentItem.description = '';
      currentItem.unitPrice = 0;
      currentItem.total = 0;
      currentItem.isTaxable = false;
      updatedItems[index] = currentItem;
      return { ...prev, items: updatedItems };
    });

    // Update search states
    setLineItemSearchStates(prev => ({
      ...prev,
      [item.id]: {
        ...prev[item.id],
        categorySearch: categoryName,
        productSearch: '',
        isCategoryFocused: false,
        filteredProducts: [] // No products available for custom category
      }
    }));
  };

  // Handle custom product text input (when user types without selecting)
  const handleProductCustomInput = (index: number, productName: string) => {
    const item = formData.items[index];

    updateFormData(prev => {
      const updatedItems = [...prev.items];
      const currentItem = { ...updatedItems[index] };
      currentItem.productId = undefined; // No ID for custom text
      currentItem.productName = productName;
      // Keep description and price empty for custom products
      currentItem.description = '';
      currentItem.unitPrice = 0;
      const quantity = currentItem.quantity > 0 ? currentItem.quantity : 1;
      currentItem.quantity = quantity;
      currentItem.total = quantity * currentItem.unitPrice;
      updatedItems[index] = currentItem;
      return { ...prev, items: updatedItems };
    });

    // Update search states
    setLineItemSearchStates(prev => ({
      ...prev,
      [item.id]: {
        ...prev[item.id],
        productSearch: productName,
        isProductFocused: false
      }
    }));
  };

  // Initialize search states when items change
  useEffect(() => {
    formData.items.forEach(item => {
      initializeLineItemSearchState(item.id);
    });
  }, [formData.items, categories, products]);

  const handleCreateNewInvoice = () => {
    // Save current invoice first
    handleSaveInvoice(false).then(() => {
      // Then reset form with new invoice number
      if (company) {
        const nextNumber = Math.max(
          ...existingInvoices.map(inv => {
            const numStr = inv.invoiceNumber.split('-').pop() || '0';
            return parseInt(numStr) || 0;
          }),
          company.invoiceSettings?.invoiceStartingNumber || 0
        ) + 1;

        setFormData(getInitialFormData({
          ...company,
          invoiceSettings: {
            ...company.invoiceSettings,
            invoiceStartingNumber: nextNumber
          }
        } as CompanySettings, searchParams));
      }
    });
  };

  useEffect(() => {
    if (!achAvailable && formData.paymentMethods.ach) {
      setFormData(prev => ({
        ...prev,
        paymentMethods: {
          ...prev.paymentMethods,
          ach: false
        }
      }));
    }
  }, [achAvailable, formData.paymentMethods.ach]);

  // Update the useEffect for company settings
  useEffect(() => {
    if (company && existingInvoices && !isEditing.isEdit) {
      const today = getLocalDateString(new Date());
      const invoiceType = searchParams.get('type') === 'recurring' ? 'recurring' : 'standard';
      
      // For recurring invoice templates, use the special numbering format
      let invoiceNumber;
      if (invoiceType === 'recurring') {
        // Get existing templates to determine next number
        const fetchTemplates = async () => {
          try {
            const templatesQuery = query(
              collection(db, 'invoiceTemplates'),
              where('companyId', '==', company.id || company.companyId)
            );
            const templatesSnapshot = await getDocs(templatesQuery);
            const templates = templatesSnapshot.docs.map(doc => doc.data());
            
            // Set the invoice number using the recurring template format
            const recNumber = generateRecurringTemplateNumber(templates);
            setFormData(prevData => ({
              ...prevData,
              invoiceNumber: recNumber
            }));
          } catch (error) {
            console.error('Error fetching templates for numbering:', error);
            // Fallback to default
            setFormData(prevData => ({
              ...prevData,
              invoiceNumber: 'REC-1'
            }));
          }
        };
        
        // Start with a default number that will be updated after fetching
        invoiceNumber = 'REC-1';
        fetchTemplates();
      } else {
        // Standard invoices use the regular numbering system
        invoiceNumber = generateInvoiceNumber(company, existingInvoices);
      }
      
      // Create full form data with all required fields
      setFormData({
        customerId: '',
        invoiceNumber,
        poNumber: '',
        date: today,
        dueDate: today,
        paymentTerms: 'due_on_receipt',
        paymentMethods: {
          credit_card: true,
          paypal: company.invoiceSettings?.paymentMethods?.paypal || false,
          ach: (company.invoiceSettings?.paymentMethods?.ach || false) && achAvailable,
          check: company.invoiceSettings?.paymentMethods?.check || false
        },
        autoPayEnabled: true, // Always true by default
        items: [],
        notes: company.invoiceSettings?.defaultNotes || '',
        footerText: company.invoiceSettings?.customFooterText || '',
        invoiceType,
        nextGenerationDate: null,
        recurringDetails: null,
        scheduledSendDate: null
      });
    }
  }, [company, existingInvoices, isEditing.isEdit, searchParams, achAvailable]);

  // Update the page title based on mode and type
  useEffect(() => {
    if (isEditing.isEdit) {
      document.title = `Edit ${getInvoiceTypeDisplayName(formData.invoiceType)} Invoice`;
    } else {
      if (formData.invoiceType === 'recurring') {
        document.title = 'New Recurring Invoice Template';
      } else {
        document.title = 'Create New Invoice';
      }
    }
  }, [isEditing.isEdit, formData.invoiceType]);

  // Update the beforeunload handler to avoid the popup when intentionally navigating
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      // If we're intentionally navigating away (to preview or similar), don't show the confirmation
      if ((window as any).isIntentionalNavigation) {
        return;
      }
      
      // Only show warning if the form is dirty (has unsaved changes)
      if (isDirty) {
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        return e.returnValue;
      }
    };

    // Add the beforeunload event listener
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    // Initialize the intentional navigation flag
    (window as any).isIntentionalNavigation = false;
    
    // Cleanup function to remove the listener when component unmounts
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      // Clean up our custom property
      delete (window as any).isIntentionalNavigation;
    };
  }, [isDirty]);

  // Function to handle email button click
  const handleEmailButtonClick = async () => {
    if (!formData.customerId) {
      showNotification({
        title: "Missing Information",
        message: "Please select a customer before continuing",
        type: "warning"
      });
      return;
    }
    
    if (formData.items.length === 0) {
      showNotification({
        title: "Missing Information",
        message: "Please add at least one item to the invoice",
        type: "warning"
      });
      return;
    }

    if (calculateTotals().total <= 0) {
      showNotification({
        title: "Invalid Total",
        message: "The invoice total must be greater than 0",
        type: "warning"
      });
      return;
    }

    if (!formData.items.every(item => item.categoryName && item.productName)) {
      showNotification({
        title: "Incomplete Items",
        message: "All items must have both a category and product selected",
        type: "warning"
      });
      return;
    }

    // Save the invoice as draft first, then show the email dialog
    const savedInvoice = await handleSaveInvoice(false, undefined, false, undefined, true);
    
    if (savedInvoice?.id) {
      navigate(`/app/invoices/preview?id=${savedInvoice.id}`);
    }
  };

  // Update getInvoiceTypeDisplayName function
  const getInvoiceTypeDisplayName = (type: 'standard' | 'recurring' | 'installment'): string => {
    switch (type) {
      case 'recurring': return 'Recurring';
      case 'installment': return 'Installment';
      default: return 'Standard';
    }
  };

  // Add a helper to determine if we're editing a template
  const isTemplateEdit = isEditing.isEdit && formData.invoiceType === 'recurring';

  // Function to handle moving to Step 2
  const handleMoveToStep2 = () => {
    if (!formData.customerId) {
      showNotification({
        title: "Missing Information",
        message: "Please select a customer before continuing",
        type: "warning"
      });
      return;
    }
    
    if (formData.items.length === 0) {
      showNotification({
        title: "Missing Information",
        message: "Please add at least one item to the invoice",
        type: "warning"
      });
      return;
    }

    if (calculateTotals().total <= 0) {
      showNotification({
        title: "Invalid Total",
        message: "The invoice total must be greater than 0",
        type: "warning"
      });
      return;
    }

    if (!formData.items.every(item => item.categoryName && item.productName)) {
      showNotification({
        title: "Incomplete Items",
        message: "All items must have both a category and product selected",
        type: "warning"
      });
      return;
    }
    
    // Ensure payment methods are properly stored for the next step
    
    // Make sure recurring details has a valid value including payment methods
    if (!formData.recurringDetails) {
      updateFormData(prev => ({
        ...prev,
        recurringDetails: {
          frequency: 'monthly',
          isActive: true,
          frequencyDetails
        }
      }));
    }
    
    setCurrentStep(2);
  };

  // Function to go back to Step 1
  const handleBackToStep1 = () => {
    setCurrentStep(1);
  };

  // Function to handle frequency type change
  const handleFrequencyTypeChange = (type: RecurringFrequencyDetails['type']) => {
    const newDetails: RecurringFrequencyDetails = {
      ...frequencyDetails,
      type
    };
    
    // Reset day-specific fields based on frequency type
    if (type === 'weekly') {
      newDetails.dayOfWeek = 'monday';
      delete newDetails.dayOfMonth;
    } else if (type === 'monthly') {
      newDetails.dayOfMonth = 1;
      newDetails.duePreference = 'first_of_month';
      delete newDetails.dayOfWeek;
    } else if (type === 'yearly') {
      // For yearly, we might want month and day
      newDetails.dayOfMonth = 1;
      delete newDetails.dayOfWeek;
    } else {
      // For empty string, reset all day-specific fields
      delete newDetails.dayOfWeek;
      delete newDetails.dayOfMonth;
    }
    
    setFrequencyDetails(newDetails);
    
    // Update the form data recurring details with the new frequency
    updateFormData(prev => ({
      ...prev,
      recurringDetails: {
        ...prev.recurringDetails!,
        frequency: mapFrequencyTypeToFormDataFrequency(type)
      }
    }));
  };
  
  // Map our detailed frequency type to the simpler form used in formData
  const mapFrequencyTypeToFormDataFrequency = (type: RecurringFrequencyDetails['type']): RecurringFrequency => {
    switch (type) {
      case '': // Handle the "Please select" case
        return 'monthly'; // Default to monthly
      case 'weekly': return 'weekly';
      case 'monthly': return 'monthly';
      case 'yearly': return 'annual';
      default: return 'monthly';
    }
  };

  // Function to calculate invoice issue date based on due date and days before
  const calculateInvoiceIssueDate = (dueDate: string, daysBeforeDue: number): string => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const dueDateObj = new Date(dueDate);
    dueDateObj.setDate(dueDateObj.getDate() - daysBeforeDue);
    
    // If the calculated date is in the past, return today's date
    if (dueDateObj < today) {
      return getLocalDateString(today);
    }
    
    return getLocalDateString(dueDateObj);
  };
  
  // Function to get the next due date based on frequency and preferences
  const calculateNextDueDate = (
    startDate: string, 
    freq: RecurringFrequencyDetails,
    iteration: number = 1
  ): string => {
    // Create new Date objects to avoid mutation
    const startDateObj = new Date(startDate);
    const nextDate = new Date(startDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    switch (freq.type) {
      case 'monthly':
        // For monthly, use the specified day of month preference
        if (freq.duePreference === 'first_of_month') {
          nextDate.setMonth(startDateObj.getMonth() + (freq.every * iteration));
          nextDate.setDate(1);
        } else if (freq.duePreference === '15th_of_month') {
          nextDate.setMonth(startDateObj.getMonth() + (freq.every * iteration));
          nextDate.setDate(15);
        } else if (freq.duePreference === 'specific_date' && typeof freq.dayOfMonth === 'number') {
          nextDate.setMonth(startDateObj.getMonth() + (freq.every * iteration));
          const lastDayOfMonth = new Date(nextDate.getFullYear(), nextDate.getMonth() + 1, 0).getDate();
          nextDate.setDate(Math.min(freq.dayOfMonth, lastDayOfMonth));
        }
        break;
        
      case 'weekly':
        // For weekly, add weeks and set to preferred day of week
        nextDate.setDate(startDateObj.getDate() + (7 * freq.every * iteration));
        if (freq.dayOfWeek) {
          const dayMap: Record<string, number> = {
            'sunday': 0, 'monday': 1, 'tuesday': 2, 'wednesday': 3,
            'thursday': 4, 'friday': 5, 'saturday': 6
          };
          const targetDay = dayMap[freq.dayOfWeek];
          const currentDay = nextDate.getDay();
          const daysToAdd = (targetDay - currentDay + 7) % 7;
          nextDate.setDate(nextDate.getDate() + daysToAdd);
        }
        break;
        
      case 'yearly':
        // For yearly, add years maintaining the same day
        nextDate.setFullYear(startDateObj.getFullYear() + (freq.every * iteration));
        break;
      
      default:
        // For empty string or any other case, return the same date
        return startDate;
    }
    
    // If the calculated date is in the past, increment until it's in the future
    while (nextDate < today) {
      switch (freq.type) {
        case 'monthly':
          nextDate.setMonth(nextDate.getMonth() + freq.every);
          break;
        case 'weekly':
          nextDate.setDate(nextDate.getDate() + (7 * freq.every));
          break;
        case 'yearly':
          nextDate.setFullYear(nextDate.getFullYear() + freq.every);
          break;
        default:
          nextDate.setDate(nextDate.getDate() + 1);
      }
    }
    
    return getLocalDateString(nextDate);
  };

  // Find the handleCompleteStep2 function in NewInvoice.tsx and completely replace it
  const handleCompleteStep2 = async () => {
    try {
      if (!user?.companyId) {
        console.error('No company ID available');
        showNotification({
          title: "Error",
          message: "Company information is missing. Please reload the page and try again.",
          type: "error"
        });
        return;
      }

      // Calculate values based on Step 2 preview logic
      const todayStr = getLocalDateString(new Date());
      const startDueDateForCalc = formData.dueDate; 
      const currentFrequencyDetails = {
        ...frequencyDetails, // from state
        type: frequencyDetails.type || 'monthly',
        every: frequencyDetails.every || 1,
        duePreference: frequencyDetails.duePreference || 'anniversary',
        invoiceIssueDaysBeforeDue: frequencyDetails.invoiceIssueDaysBeforeDue ?? 0
      };

      const calculatedFirstInvoiceIssueDate = calculateInvoiceIssueDate(
        startDueDateForCalc,
        currentFrequencyDetails.invoiceIssueDaysBeforeDue
      );
      const displayFirstInvoiceIssueDate = calculatedFirstInvoiceIssueDate < todayStr
        ? todayStr
        : calculatedFirstInvoiceIssueDate;

      const nextDueDateForCalc = calculateNextDueDate(startDueDateForCalc, currentFrequencyDetails);
      const nextInvoiceIssueDate = calculateInvoiceIssueDate(
        nextDueDateForCalc,
        currentFrequencyDetails.invoiceIssueDaysBeforeDue
      );

      let finalNextGenerationDate;
      if (displayFirstInvoiceIssueDate > todayStr) {
        finalNextGenerationDate = displayFirstInvoiceIssueDate;
      } else { 
        finalNextGenerationDate = nextInvoiceIssueDate;
      }

      // If we're in editing mode, handle as an update instead of a new template
      if (isEditingTemplate && id) {
        
        // Pass the calculated nextGenerationDate to handleSaveInvoice
        await handleSaveInvoice(false, undefined, false, finalNextGenerationDate);
        
        // Navigate directly to the preview URL
        navigate(`/app/invoices/preview?id=${id}`);
        return;
      }

      // Add better debug logging

      // Find the selected customer from the customers list
      const selectedCustomer = customers.find(c => c.id === formData.customerId);
      
      if (!selectedCustomer) {
        console.error('No customer selected', { formData });
        showNotification({
          title: "Error",
          message: "Please select a valid customer before proceeding.",
          type: "error"
        });
        return;
      }

      // Ensure payment methods are properly captured
      const enabledPaymentMethods = Object.entries(formData.paymentMethods)
        .filter(([_, enabled]) => enabled)
        .map(([method]) => method as 'credit_card' | 'paypal' | 'ach' | 'check');
        
      
      const hasACH = formData.paymentMethods.ach === true;

      const templateData: any = {
        companyId: user?.companyId || '',
        invoiceNumber: formData.invoiceNumber,
        customerId: formData.customerId,
        customerName: `${selectedCustomer.firstName || ''} ${selectedCustomer.lastName || ''}`.trim() || 'Unknown Customer',
        customerCompanyName: selectedCustomer.companyName || '',
        customerEmail: selectedCustomer.email || '',
        customerAddress: `${selectedCustomer.street || ''}, ${selectedCustomer.city || ''}, ${selectedCustomer.state || ''} ${selectedCustomer.zip || ''}`,
        street: selectedCustomer.street || '',
        city: selectedCustomer.city || '',
        state: selectedCustomer.state || '',
        zip: selectedCustomer.zip || '',
        date: formData.date,
        dueDate: formatDateForDB(formData.dueDate),
        items: formData.items,
        subtotal: calculateTotals().subtotal,
        taxRate: (company?.defaultTaxRate ?? 0) / 100,
        taxAmount: calculateTotals().taxAmount,
        platformFee: (company?.platformFee ?? 0),
        total: calculateTotals().total,
        status: 'inactive', 
        notes: formData.notes,
        footerText: formData.footerText,
        paymentMethods: enabledPaymentMethods,
        paymentKey: `pay_${uuidv4().replace(/-/g, '')}`,
        invoiceType: 'recurring',
        nextGenerationDate: finalNextGenerationDate,
        autoPayEnabled: formData.autoPayEnabled === true,
        recurringDetails: {
          frequency: mapFrequencyTypeToFormDataFrequency(currentFrequencyDetails.type),
          endDate: null, 
          remainingOccurrences: null,
          isActive: false, 
          frequencyDetails: currentFrequencyDetails, 
          enableACH: hasACH 
        },
        schedulePreview: {
          frequency: `Every ${currentFrequencyDetails.every} ${currentFrequencyDetails.type}${currentFrequencyDetails.type === 'weekly' ? `(s) on ${currentFrequencyDetails.dayOfWeek}` : ''}${
            currentFrequencyDetails.type === 'monthly' ? 
              currentFrequencyDetails.duePreference === 'first_of_month' ? ' on the 1st' :
              currentFrequencyDetails.duePreference === '15th_of_month' ? ' on the 15th' :
              currentFrequencyDetails.duePreference === 'specific_date' ? ` on the ${currentFrequencyDetails.dayOfMonth}${currentFrequencyDetails.dayOfMonth === 1 ? 'st' : currentFrequencyDetails.dayOfMonth === 2 ? 'nd' : currentFrequencyDetails.dayOfMonth === 3 ? 'rd' : 'th'}` :
              ' on the monthly anniversary'
            : ''
          }`,
          firstInvoiceDate: calculatedFirstInvoiceIssueDate,
          firstPaymentDue: startDueDateForCalc,
          secondInvoiceDate: nextInvoiceIssueDate,
          secondPaymentDue: nextDueDateForCalc,
          invoiceGenerationDays: currentFrequencyDetails.invoiceIssueDaysBeforeDue
        }
      };
      
      
      const cleanedData = JSON.parse(JSON.stringify(templateData));
      
      
      const { createInvoiceTemplate } = await import('@/lib/api');
      
      const savedTemplate = await createInvoiceTemplate(cleanedData);
      
      if (savedTemplate && savedTemplate.id) {
        
        setIsDirty(false);
        setSavedInvoiceData(savedTemplate as unknown as Invoice);
        
        showNotification({
          title: "Success",
          message: "Template created as inactive. Review and activate from the invoices list when ready.",
          type: "success"
        });
        
        sessionStorage.setItem('savedInvoiceId', savedTemplate.id);
        sessionStorage.setItem('invoiceCreationSuccess', 'true');
        localStorage.setItem('savedInvoiceId', savedTemplate.id);
        localStorage.setItem('invoiceCreationSuccess', 'true');
        
        navigate(`/app/invoices/preview?id=${savedTemplate.id}`);
      } else {
        console.error('Failed to save template or no ID returned');
        showNotification({
          title: "Error",
          message: "Could not save the template. Please try again.",
          type: "error"
        });
      }
    } catch (err) {
      console.error('Error saving template:', err);
      
      let errorMessage = "Failed to save template. Please try again.";
      if (err instanceof Error) {
        console.error('Error details:', {
          name: err.name,
          message: err.message,
          stack: err.stack
        });
        errorMessage = `Error: ${err.message}`;
      }
      
      showNotification({
        title: "Error",
        message: errorMessage,
        type: "error"
      });
    }
  };

  // --- Stepper/Progress Indicator ---
  const Stepper = ({ currentStep, steps }: { currentStep: number; steps: string[] }) => (
    <div className="flex items-center mb-8">
      {steps.map((step, idx) => (
        <div key={step} className="flex items-center">
          <div className={
            `rounded-full w-8 h-8 flex items-center justify-center font-bold text-white ${
              idx + 1 === currentStep ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-700'
            }`
          }>
            {idx + 1}
          </div>
          <span className={`ml-2 mr-4 text-base font-medium ${idx + 1 === currentStep ? 'text-primary' : 'text-gray-500 dark:text-gray-400'}`}>{step}</span>
          {idx < steps.length - 1 && <div className="w-8 h-1 bg-gray-200 dark:bg-gray-700 rounded-full mx-2" />}
        </div>
      ))}
    </div>
  );

  // Add these at the component level, before renderStep2
  const calculateInitialDueDate = (
    frequencyType: string,
    duePreference: string | undefined,
    dayOfMonth: number | string | undefined,
    dayOfWeek: string | undefined
  ): string => {
    const today = new Date();
    const initialDate = new Date();
    
    switch (frequencyType) {
      case 'monthly':
        if (duePreference === 'first_of_month') {
          initialDate.setDate(1);
          if (initialDate < today) {
            initialDate.setMonth(initialDate.getMonth() + 1);
          }
        } else if (duePreference === '15th_of_month') {
          initialDate.setDate(15);
          if (initialDate < today) {
            initialDate.setMonth(initialDate.getMonth() + 1);
          }
        } else if (duePreference === 'specific_date' && dayOfMonth !== undefined) {
          // Convert dayOfMonth to number if it's a string
          const dayOfMonthNumber = typeof dayOfMonth === 'string' ? parseInt(dayOfMonth, 10) : dayOfMonth;
          if (!isNaN(dayOfMonthNumber)) {
            initialDate.setDate(dayOfMonthNumber);
            if (initialDate < today) {
              initialDate.setMonth(initialDate.getMonth() + 1);
            }
          }
        }
        break;
        
      case 'weekly':
        if (dayOfWeek) {
          const dayMap: Record<string, number> = {
            'sunday': 0, 'monday': 1, 'tuesday': 2, 'wednesday': 3,
            'thursday': 4, 'friday': 5, 'saturday': 6
          };
          const targetDay = dayMap[dayOfWeek];
          const currentDay = today.getDay();
          const daysToAdd = (targetDay - currentDay + 7) % 7;
          initialDate.setDate(today.getDate() + daysToAdd);
        }
        break;
        
      case 'daily':
        if (initialDate < today) {
          initialDate.setDate(today.getDate() + 1);
        }
        break;
    }
    
    return getLocalDateString(initialDate);
  };

  // Add this effect at the component level
  useEffect(() => {
    if (formData.invoiceType === 'recurring') {
      const initialDueDate = calculateInitialDueDate(
        frequencyDetails.type,
        frequencyDetails.duePreference,
        frequencyDetails.dayOfMonth,
        frequencyDetails.dayOfWeek
      );
      updateFormData(prev => ({
        ...prev,
        dueDate: initialDueDate
      }));
    }
  }, [formData.invoiceType, frequencyDetails.type, frequencyDetails.duePreference, frequencyDetails.dayOfMonth, frequencyDetails.dayOfWeek]);

  // Update renderStep2 to remove the hooks
  const renderStep2 = () => {
    // Calculate preview dates
    const startDueDate = formData.dueDate;
    
    // Calculate first invoice issue date
    const calculatedFirstInvoiceIssueDate = calculateInvoiceIssueDate(
      startDueDate, 
      frequencyDetails.invoiceIssueDaysBeforeDue
    );

    // Calculate next due date by adding the frequency period
    const nextDueDate = calculateNextDueDate(startDueDate, frequencyDetails);
    
    // Calculate next invoice issue date based on the next due date
    const calculatedNextInvoiceIssueDate = calculateInvoiceIssueDate(
      nextDueDate, 
      frequencyDetails.invoiceIssueDaysBeforeDue
    );

    // Get today's date for comparisons
    const todayStr = getLocalDateString(new Date());
    
    // Determine display dates, ensuring they're not in the past
    const displayFirstInvoiceIssueDate = calculatedFirstInvoiceIssueDate < todayStr 
      ? todayStr 
      : calculatedFirstInvoiceIssueDate;
    
    const displayNextInvoiceIssueDate = calculatedNextInvoiceIssueDate < todayStr 
      ? todayStr 
      : calculatedNextInvoiceIssueDate;

    // Only show manual date picker for daily and weekly frequencies
    const showFirstPaymentDue = frequencyDetails.type === 'weekly';

    return (
      <div className="space-y-8">
        {/* Stepper/Progress Bar */}
        <Stepper currentStep={2} steps={["Invoice Details", "Payment Schedule"]} />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Card */}
          <div className="lg:col-span-2">
            <Card className="p-8 shadow-xl border border-gray-200/30 dark:border-gray-800/30 bg-white dark:bg-gray-900">
              <div className="space-y-10">
                <h2 className="text-2xl font-bold text-primary flex items-center gap-2 mb-6">
                  <span className="inline-block w-2 h-8 bg-primary rounded mr-2"></span>
                  Payment Schedule
                </h2>
                {/* Frequency Section */}
                <div className="space-y-2 border-b pb-6">
                  <Label className="text-lg font-semibold">How often should the customer be billed?</Label>
                  <div className="flex flex-wrap items-center gap-4 mt-2">
                    <span className="text-gray-700 dark:text-gray-300">Every</span>
                    <Input
                      type="number"
                      min="1"
                      max="99"
                      value={frequencyDetails.every}
                      onChange={(e) => setFrequencyDetails(prev => ({
                        ...prev,
                        every: parseInt(e.target.value) || 1
                      }))}
                      className="w-20 text-lg font-medium"
                    />
                    <Select
                      value={frequencyDetails.type}
                      onValueChange={(value) => handleFrequencyTypeChange(value as RecurringFrequencyDetails['type'])}
                    >
                      <SelectTrigger className="w-48 text-lg font-medium">
                        <SelectValue placeholder="Please select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="weekly">Week(s)</SelectItem>
                        <SelectItem value="monthly">Month(s)</SelectItem>
                        <SelectItem value="yearly">Year(s)</SelectItem>
                      </SelectContent>
                    </Select>
                    
                    {/* Show day of week selector for weekly frequency */}
                    {frequencyDetails.type === 'weekly' && (
                      <>
                        <span className="ml-2 text-gray-700 dark:text-gray-300">on</span>
                        <Select
                          value={frequencyDetails.dayOfWeek || 'monday'}
                          onValueChange={(value) => {
                            setFrequencyDetails(prev => ({
                              ...prev,
                              dayOfWeek: value as RecurringFrequencyDetails['dayOfWeek'],
                              duePreference: 'day_of_week'
                            }));
                          }}
                        >
                          <SelectTrigger className="w-32">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="monday">Monday</SelectItem>
                            <SelectItem value="tuesday">Tuesday</SelectItem>
                            <SelectItem value="wednesday">Wednesday</SelectItem>
                            <SelectItem value="thursday">Thursday</SelectItem>
                            <SelectItem value="friday">Friday</SelectItem>
                            <SelectItem value="saturday">Saturday</SelectItem>
                            <SelectItem value="sunday">Sunday</SelectItem>
                          </SelectContent>
                        </Select>
                      </>
                    )}
                    
                    {/* Show day of month selector for monthly frequency */}
                    {frequencyDetails.type === 'monthly' && (
                      <>
                        <span className="ml-2 text-gray-700 dark:text-gray-300">on the</span>
                        <Select
                          value={frequencyDetails.duePreference}
                          onValueChange={(value) => {
                            const newPreference = value as RecurringFrequencyDetails['duePreference'];
                            let dayOfMonth = frequencyDetails.dayOfMonth;
                            if (newPreference === 'first_of_month') dayOfMonth = 1;
                            else if (newPreference === '15th_of_month') dayOfMonth = 15;
                            setFrequencyDetails(prev => ({
                              ...prev,
                              duePreference: newPreference,
                              dayOfMonth
                            }));
                          }}
                        >
                          <SelectTrigger className="w-48">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="first_of_month">1st of the month</SelectItem>
                            <SelectItem value="15th_of_month">15th of the month</SelectItem>
                            <SelectItem value="specific_date">Specific day of month</SelectItem>
                          </SelectContent>
                        </Select>
                        {frequencyDetails.duePreference === 'specific_date' && (
                          <Select
                            value={typeof frequencyDetails.dayOfMonth === 'number' ? frequencyDetails.dayOfMonth.toString() : '1'}
                            onValueChange={(value) => {
                              setFrequencyDetails(prev => ({
                                ...prev,
                                dayOfMonth: parseInt(value)
                              }));
                            }}
                          >
                            <SelectTrigger className="w-32">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="max-h-60 overflow-y-auto">
                              {Array.from({ length: 28 }, (_, i) => i + 1).map(day => (
                                <SelectItem key={day} value={day.toString()}>
                                  {day}{day === 1 ? 'st' : day === 2 ? 'nd' : day === 3 ? 'rd' : 'th'}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* First Payment Due Section - Only for daily and weekly */}
                {showFirstPaymentDue && (
                  <div className="space-y-2 border-b pb-6">
                    <Label htmlFor="firstDueDate" className="text-lg font-semibold">
                      When should the first payment be due?
                    </Label>
                    <Input
                      id="firstDueDate"
                      type="date"
                      value={formData.dueDate}
                      onChange={(e) => {
                        const todayStr = getLocalDateString(new Date());
                        if (e.target.value < todayStr) {
                          updateFormData(prev => ({ ...prev, dueDate: todayStr }));
                          showNotification({
                            title: 'Invalid Date',
                            message: 'You cannot select a date in the past. The date has been set to today.',
                            type: 'warning',
                          });
                        } else {
                          updateFormData(prev => ({ ...prev, dueDate: e.target.value }));
                        }
                      }}
                      className="w-full md:w-1/2 text-lg font-medium"
                      min={getLocalDateString(new Date())}
                    />
                    <p className="text-sm text-gray-500 mt-1">
                      This is when the first payment will be due
                    </p>
                  </div>
                )}

                {/* Invoice Generation Timing Section */}
                <div className="space-y-2 border-b pb-6">
                  <Label className="text-lg font-semibold">When should invoices be generated?</Label>
                  <div className="flex items-center gap-3 mt-2">
                    <Input
                      type="number"
                      min="0"
                      max="30"
                      value={frequencyDetails.invoiceIssueDaysBeforeDue}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Allow empty string while typing
                        if (value === '') {
                          setFrequencyDetails(prev => ({
                            ...prev,
                            invoiceIssueDaysBeforeDue: 0
                          }));
                        } else {
                          const numValue = parseInt(value);
                          if (!isNaN(numValue) && numValue >= 0 && numValue <= 30) {
                            setFrequencyDetails(prev => ({
                              ...prev,
                              invoiceIssueDaysBeforeDue: numValue
                            }));
                          }
                        }
                      }}
                      onBlur={(e) => {
                        // Ensure we have a valid value when field loses focus
                        const value = parseInt(e.target.value);
                        if (isNaN(value) || value < 0 || value > 30) {
                          setFrequencyDetails(prev => ({
                            ...prev,
                            invoiceIssueDaysBeforeDue: 0
                          }));
                        }
                      }}
                      className="w-20 text-lg font-medium"
                    />
                    <span className="text-gray-700 dark:text-gray-300">days before the due date</span>
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    Specify how many days before the due date the invoice should be generated and sent. Use 0 to generate invoices on the due date.
                  </p>
                </div>
              </div>
            </Card>
          </div>

          {/* Summary Card */}
          <div className="lg:col-span-1">
            {frequencyDetails.type !== '' && ( // Simplified conditional rendering
              <Card className="p-6 shadow-xl border-2 border-primary/30 bg-gradient-to-br from-primary/5 to-white dark:from-primary/10 dark:to-gray-900">
                <div className="flex items-center gap-3 mb-4">
                  <svg className="w-7 h-7 text-primary" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <h3 className="text-lg font-bold text-primary">Schedule Preview</h3>
                </div>
                <div className="space-y-3 text-gray-700 dark:text-gray-200">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">Frequency:</span>
                    <span>
                      Every {frequencyDetails.every} {
                        frequencyDetails.type === 'weekly' ? `week(s) on ${frequencyDetails.dayOfWeek || 'Monday'}` :
                        frequencyDetails.type === 'monthly' ? 
                          frequencyDetails.duePreference === 'first_of_month' ? 'month(s) on the 1st' :
                          frequencyDetails.duePreference === '15th_of_month' ? 'month(s) on the 15th' :
                          frequencyDetails.duePreference === 'specific_date' ? `month(s) on the ${frequencyDetails.dayOfMonth}${frequencyDetails.dayOfMonth === 1 ? 'st' : frequencyDetails.dayOfMonth === 2 ? 'nd' : frequencyDetails.dayOfMonth === 3 ? 'rd' : 'th'}` :
                          ' on the monthly anniversary'
                        : frequencyDetails.type === 'yearly' ? 'year(s) on the anniversary date' 
                        : ''
                      }
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">First Invoice:</span>
                    <span>{parseDatePreservingDay(displayFirstInvoiceIssueDate).toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">First Payment Due:</span>
                    <span>{parseDatePreservingDay(startDueDate).toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">2nd Invoice:</span>
                    <span>{parseDatePreservingDay(displayNextInvoiceIssueDate).toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">2nd Payment Due:</span>
                    <span>{parseDatePreservingDay(nextDueDate).toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">Invoice Generation:</span>
                    <span>{frequencyDetails.invoiceIssueDaysBeforeDue} days before due date</span>
                  </div>
                </div>
                <div className="mt-6 flex flex-col gap-2">
                  <div className="flex justify-end gap-2">
                    <Button 
                      variant="outline" 
                      className="flex items-center gap-2 border-primary/50 text-primary hover:bg-primary/10"
                      onClick={handleBackToStep1}
                    >
                      <ArrowLeft className="w-4 h-4" />
                      Back
                    </Button>
                    <Button 
                      onClick={handleCompleteStep2}
                      className="bg-primary text-primary-foreground hover:bg-primary/90 flex items-center gap-2 font-semibold shadow-lg px-6 py-2"
                    >
                      {isEditingTemplate ? 'Save and Preview' : 'Continue'}
                      <ArrowRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Function to handle save and preview button
  const handleSaveAndPreview = async () => {
    await handleSaveInvoice(false, undefined, true);
  };

  // Inside the NewInvoice component, add the handlers
  const handlePaymentMethodChange = (method: keyof InvoiceFormData['paymentMethods'], value: boolean) => {
    if (method === 'ach' && !achAvailable) {
      return;
    }

    updateFormData(prev => ({
      ...prev,
      paymentMethods: {
        ...prev.paymentMethods,
        [method]: value
      }
    }));
  };

  const handleAutoPayChange = (enabled: boolean) => {
    updateFormData(prev => ({
      ...prev,
      autoPayEnabled: enabled
    }));
  };

  // Update the main container with improved styling
  return (
    <div className="max-w-7xl mx-auto py-8 px-4 space-y-8">
      {isLoadingInvoice && id ? (
        <div className="space-y-4">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Loading Invoice...</h1>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Please wait while we load the invoice data</p>
            </div>
          </div>
          <div className="animate-pulse space-y-4">
            <div className="h-12 bg-gray-200 dark:bg-gray-800 rounded"></div>
            <div className="h-64 bg-gray-200 dark:bg-gray-800 rounded"></div>
          </div>
        </div>
      ) : (
        <>
          {formData.invoiceType === 'recurring' && currentStep === 1 && (
            <Stepper currentStep={1} steps={["Invoice Details", "Payment Schedule"]} />
          )}
          {formData.invoiceType === 'recurring' && currentStep === 2 ? (
            // Show Step 2 for recurring invoices
            renderStep2()
          ) : (
            // Show regular invoice form (Step 1)
            <>
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-8">
                <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
                  <div>
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                      {isEditing.isEdit 
                        ? `Edit ${getInvoiceTypeDisplayName(formData.invoiceType)} Invoice` 
                        : formData.invoiceType === 'recurring'
                          ? 'New Recurring Invoice Template'
                          : 'Create New Invoice'}
                    </h1>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                      {isEditing.isEdit 
                        ? 'Update the invoice details below' 
                        : formData.invoiceType === 'recurring'
                          ? 'Fill in the details below to create a new template'
                          : 'Fill in the details below to create a new invoice'}
                    </p>
                  </div>
                  <div className="flex flex-wrap md:flex-nowrap gap-3 mt-4 md:mt-0">
                    <Button variant="outline" onClick={() => navigate('/app/invoices')} 
                      className="text-gray-600 hover:text-gray-800 border-gray-300 hover:bg-gray-50">
                      Cancel
                    </Button>
                    {/* Add validation check for Save Draft button */}
                    <Button
                      onClick={() => handleSaveInvoice(false, undefined, false, undefined, true)}
                      variant={isSaved ? "outline" : "default"}
                      className={cn(
                        "transition-colors",
                        isSaved
                          ? "bg-green-500 text-white hover:bg-green-600"
                          : "bg-primary/90 hover:bg-primary text-white"
                      )}
                      disabled={saving || !formData.customerId || formData.items.length === 0 || calculateTotals().total <= 0 || !formData.items.every(item => item.categoryName && item.productName)}
                    >
                      {saving ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Saving...
                        </>
                      ) : isSaved ? (
                        "Saved"
                      ) : formData.invoiceType === 'recurring' ? (
                        'Save Recurring Invoice'
                      ) : (
                        'Save Draft'
                      )}
                    </Button>
                    {isEditing.isEdit ? (
                      <Button
                        onClick={isEditingTemplate && formData.invoiceType === 'recurring' ?
                          () => setCurrentStep(2) : handleSaveAndPreview}
                        className="bg-primary text-white hover:bg-primary/90 font-medium shadow-sm"
                        disabled={saving || !formData.customerId || formData.items.length === 0 || calculateTotals().total <= 0 || !formData.items.every(item => item.categoryName && item.productName)}
                      >
                        {saving ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            {isEditingTemplate && formData.invoiceType === 'recurring' ? 'Saving...' : 'Loading...'}
                          </>
                        ) : (
                          isEditingTemplate && formData.invoiceType === 'recurring' ?
                            'Continue' : 'Preview Invoice'
                        )}
                      </Button>
                    ) : (
                      <Button
                        onClick={formData.invoiceType === 'recurring' && !isEditing.isEdit ? handleMoveToStep2 : handleEmailButtonClick}
                        className="bg-primary text-white hover:bg-primary/90 font-medium shadow-sm"
                        disabled={saving || !formData.customerId || formData.items.length === 0 || calculateTotals().total <= 0 || !formData.items.every(item => item.categoryName && item.productName)}
                      >
                        {saving ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Loading...
                          </>
                        ) : (
                          'Continue'
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              {/* Invoice Activity Timeline */}
              {isEditing.isEdit && editingInvoice?.activities && editingInvoice.activities.length > 0 && (
                <Card className="p-6 mb-6 shadow-sm border border-gray-200/50 dark:border-gray-700/50">
                  <InvoiceActivityTimeline activities={editingInvoice.activities} />
                </Card>
              )}

              {/* Remove the recurring invoice type section from Step 1 */}
              {/* Main Content Card */}
              <Card className="p-6 shadow-sm border border-gray-200/50 dark:border-gray-700/50 bg-white dark:bg-gray-800 overflow-hidden">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Left Column - Customer & Invoice Details */}
                  <div className="space-y-6">
                    <div className="space-y-4">
                      <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 flex items-center">
                        <span className="inline-block w-1.5 h-5 bg-blue-500 rounded-sm mr-2"></span>
                        Customer Information
                      </h2>
                      <div className="space-y-2">
                        <div className="relative">
                          <CustomerSearch
                            customers={customers}
                            value={customerSearchTerm}
                            onChange={handleCustomerSearch}
                            onSelect={(customer) => {
                              setFormData(prev => ({ ...prev, customerId: customer.id }));
                              setCustomerSearchTerm(customer.companyName || `${customer.firstName} ${customer.lastName}`);
                              // Add first line item if none exist
                              if (formData.items.length === 0 && !isEditing.isEdit && !isEditingTemplate) {
                                addLineItem();
                              }
                            }}
                            onAddNew={() => setIsAddCustomerDialogOpen(true)}
                            className="w-full border-gray-300 dark:border-gray-600 focus:ring-primary/30 focus:border-primary/70"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 flex items-center">
                        <span className="inline-block w-1.5 h-5 bg-indigo-500 rounded-sm mr-2"></span>
                        Invoice Details
                      </h2>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            {formData.invoiceType === 'recurring' ? 'Template Number' : 'Invoice Number'}
                          </Label>
                          <Input
                            value={formData.invoiceNumber}
                            onChange={(e) => {
                              setFormData(prev => ({ ...prev, invoiceNumber: e.target.value }));
                              setIsDirty(true);
                            }}
                            className="bg-gray-50/70 dark:bg-gray-800/70 border-gray-300 dark:border-gray-600 focus:ring-primary/30 focus:border-primary/70"
                            placeholder={company ? generateInvoiceNumber(company as CompanySettings) : ''}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">P.O. Number</Label>
                          <Input
                            value={formData.poNumber}
                            onChange={(e) => setFormData(prev => ({ ...prev, poNumber: e.target.value }))}
                            placeholder="Optional"
                            className="bg-gray-50/70 dark:bg-gray-800/70 border-gray-300 dark:border-gray-600 focus:ring-primary/30 focus:border-primary/70"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Right Column - Dates & Payment */}
                  <div className="space-y-6">
                    {/* Only show Payment & Dates title and fields for standard and installment invoices */}
                    {(formData.invoiceType === 'standard' || formData.invoiceType === 'installment') && (
                      <div className="space-y-4">
                        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 flex items-center">
                          <span className="inline-block w-1.5 h-5 bg-green-500 rounded-sm mr-2"></span>
                          Payment & Dates
                        </h2>
                        <div className="grid grid-cols-1 gap-4">
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                            {formData.invoiceType === 'standard' && (
                              <div className="space-y-2">
                                <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Invoice Date</Label>
                                <Input
                                  type="date"
                                  value={formData.date}
                                  onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                                  className="bg-gray-50/70 dark:bg-gray-800/70 border-gray-300 dark:border-gray-600 focus:ring-primary/30 focus:border-primary/70"
                                />
                              </div>
                            )}
                            {/* Only show payment terms if not a recurring invoice */}
                            {!(editingInvoice as InvoiceWithMetadata)?.metadata?.isRecurring && (
                              <div className="space-y-2">
                                <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Payment Terms</Label>
                                <Select
                                  value={formData.paymentTerms}
                                  onValueChange={handlePaymentTermsChange}
                                >
                                  <SelectTrigger className="bg-gray-50/70 dark:bg-gray-800/70 border-gray-300 dark:border-gray-600 focus:ring-primary/30 focus:border-primary/70 focus:ring-offset-0">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {PAYMENT_TERMS.map(term => (
                                      <SelectItem key={term.value} value={term.value}>
                                        {term.label}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            )}
                            <div className="space-y-2">
                              <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Due Date</Label>
                              <Input
                                type="date"
                                value={formData.dueDate}
                                onChange={(e) => {
                                  // Only allow direct changes to due date if it's a recurring invoice
                                  if ((editingInvoice as InvoiceWithMetadata)?.metadata?.isRecurring) {
                                    setFormData(prev => ({ ...prev, dueDate: e.target.value }));
                                  } else {
                                    // For non-recurring invoices, use the payment terms calculation
                                    handlePaymentTermsChange(formData.paymentTerms);
                                  }
                                }}
                                className="bg-gray-50/70 dark:bg-gray-800/70 border-gray-300 dark:border-gray-600 focus:ring-primary/30 focus:border-primary/70"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    <div className="space-y-4">
                      {/* Payment Methods Section */}
                      <div className="space-y-4">
                        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 flex items-center">
                          <span className="inline-block w-1.5 h-5 bg-purple-500 rounded-sm mr-2"></span>
                          Payment Methods
                        </h2>
                        <Card className="p-4 bg-gray-50/80 dark:bg-gray-800/80 border border-gray-200/70 dark:border-gray-700/70 shadow-sm">
                          <div className="flex items-center gap-6">
                            {/* Credit Card */}
                            {company?.invoiceSettings?.paymentMethods?.credit_card && (
                              <div className="flex items-center gap-2">
                                <Checkbox
                                  id="credit_card"
                                  checked={formData.paymentMethods.credit_card}
                                  onCheckedChange={(checked) => {
                                    setFormData(prev => ({
                                      ...prev,
                                      paymentMethods: {
                                        ...prev.paymentMethods,
                                        credit_card: checked as boolean
                                      }
                                    }));
                                  }}
                                />
                                <Label htmlFor="credit_card" className="text-sm">Credit Card</Label>
                              </div>
                            )}

                            {/* ACH */}
                            {company?.invoiceSettings?.paymentMethods?.ach && achAvailable && (
                              <div className="flex items-center gap-2">
                                <Checkbox
                                  id="ach"
                                  checked={formData.paymentMethods.ach}
                                  onCheckedChange={(checked) => {
                                    setFormData(prev => ({
                                      ...prev,
                                      paymentMethods: {
                                        ...prev.paymentMethods,
                                        ach: checked as boolean
                                      }
                                    }));
                                  }}
                                />
                                <Label htmlFor="ach" className="text-sm">ACH</Label>
                              </div>
                            )}

                            {/* AutoPay with tooltip */}
                            <TooltipProvider>
                              <div className="flex items-center gap-2">
                                <Checkbox
                                  id="autopay"
                                  checked={formData.autoPayEnabled}
                                  onCheckedChange={(checked) => {
                                    setFormData(prev => ({
                                      ...prev,
                                      autoPayEnabled: checked as boolean
                                    }));
                                  }}
                                />
                                <Label htmlFor="autopay" className="text-sm">AutoPay</Label>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <InfoCircledIcon className="h-4 w-4 text-muted-foreground cursor-help" />
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>Process payment with saved payment method automatically</p>
                                  </TooltipContent>
                                </Tooltip>
                              </div>
                            </TooltipProvider>
                          </div>
                        </Card>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>

              {/* Line Items Card */}
              <Card className="p-6 shadow-sm border border-gray-200/50 dark:border-gray-700/50 bg-white dark:bg-gray-800 min-h-[480px]">
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 flex items-center">
                      <span className="inline-block w-1.5 h-5 bg-amber-500 rounded-sm mr-2"></span>
                      Line Items
                    </h2>
                    <Button
                      onClick={addLineItem}
                      variant="outline"
                      className="flex items-center gap-2 border-primary/30 text-primary hover:bg-primary/5 hover:text-primary-dark"
                    >
                      <Plus className="w-4 h-4" />
                      Add Item
                    </Button>
                  </div>

                  <div className="overflow-x-auto overflow-y-visible relative rounded-lg border border-gray-200/70 dark:border-gray-700/70">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="bg-gray-50 dark:bg-gray-800/90 border-b border-gray-200 dark:border-gray-700">
                          <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400 w-[15%]">Category</th>
                          <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400 w-[15%]">Product</th>
                          <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Description</th>
                          <th className="px-3 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400 w-[6%]">Qty</th>
                          <th className="px-3 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400 w-[8%]">Price</th>
                          <th className="px-3 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400 w-[8%]">Total</th>
                          <th className="px-3 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400 w-[6%]">Tax</th>
                          <th className="px-3 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400 w-[4%]"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {formData.items.length === 0 && (
                          <tr>
                            <td colSpan={8} className="px-3 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                              No items added yet. Click "Add Item" to add your first line item.
                            </td>
                          </tr>
                        )}
                        {formData.items.map((item, index) => (
                          <tr key={item.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                            <td className="px-3 py-2 relative">
                              <CategorySearch
                                categories={categories}
                                value={lineItemSearchStates[item.id]?.categorySearch || item.categoryName}
                                onChange={(value) => handleCategorySearch(item.id, value)}
                                onSelect={(category) => handleCategorySelect(index, category.id)}
                                onAddNew={() => handleAddNewCategory(index)}
                                onCustomInput={(value) => handleCategoryCustomInput(index, value)}
                                className="h-8 bg-transparent border-gray-300 dark:border-gray-600 focus:ring-primary/30 focus:border-primary/70"
                              />
                            </td>

                            <td className="px-3 py-2 relative">
                              <ProductSearch
                                products={products}
                                value={lineItemSearchStates[item.id]?.productSearch || item.productName}
                                onChange={(value) => handleProductSearch(item.id, value, item.categoryId)}
                                onSelect={(product) => handleProductSelect(index, product.id)}
                                onAddNew={() => handleAddNewProduct(item.categoryId, index)}
                                onCustomInput={(value) => handleProductCustomInput(index, value)}
                                className="h-8 bg-transparent border-gray-300 dark:border-gray-600 focus:ring-primary/30 focus:border-primary/70"
                                disabled={!item.categoryId && !item.categoryName && products.length > 0 && categories.length > 0}
                                categoryId={item.categoryId}
                                categoryName={item.categoryName}
                              />
                            </td>
                            
                            <td className="px-3 py-2">
                              <Input
                                value={item.description}
                                onChange={(e) => handleLineItemChange(index, 'description', e.target.value)}
                                className="bg-transparent h-8 border-gray-300 dark:border-gray-600 focus:ring-primary/30 focus:border-primary/70"
                                placeholder="Description..."
                              />
                            </td>
                            
                            <td className="px-3 py-2">
                              <Input
                                type="number"
                                min="1"
                                value={item.quantity.toString()}
                                onChange={(e) => handleLineItemChange(index, 'quantity', parseFloat(e.target.value) || 0)}
                                className="bg-transparent h-8 border-gray-300 dark:border-gray-600 focus:ring-primary/30 focus:border-primary/70 text-center no-spinner"
                              />
                            </td>
                            
                            <td className="px-3 py-2">
                              <Input
                                type="number"
                                min="0"
                                step="0.01"
                                value={item.unitPrice.toString()}
                                onChange={(e) => handleLineItemChange(index, 'unitPrice', parseFloat(e.target.value) || 0)}
                                className="bg-transparent h-8 border-gray-300 dark:border-gray-600 focus:ring-primary/30 focus:border-primary/70 text-right no-spinner"
                              />
                            </td>
                            
                            <td className="px-3 py-2">
                              <div className="text-right font-medium">
                                ${item.total.toFixed(2)}
                              </div>
                            </td>
                            
                            <td className="px-3 py-2 text-center">
                              <Checkbox
                                checked={item.isTaxable}
                                onCheckedChange={(checked) => handleLineItemChange(index, 'isTaxable', !!checked)}
                                className="data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                              />
                            </td>
                            
                            <td className="px-3 py-2 text-center">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => removeLineItem(index)}
                                className="h-8 w-8 text-gray-500 hover:text-red-600 hover:bg-gray-100 dark:hover:bg-gray-800"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  {/* Totals section - add styling to improve */}
                  <div className="mt-6 flex flex-col items-end">
                    <div className="w-full max-w-xs space-y-2 text-right">
                      <div className="flex justify-between text-sm">
                        <span className="font-medium text-gray-500 dark:text-gray-400">Subtotal:</span>
                        <span className="font-medium text-gray-900 dark:text-gray-100">${calculateTotals().subtotal.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="font-medium text-gray-500 dark:text-gray-400">Tax ({(company?.defaultTaxRate ?? 0).toFixed(1)}%):</span>
                        <span className="font-medium text-gray-900 dark:text-gray-100">${calculateTotals().taxAmount.toFixed(2)}</span>
                      </div>
                      {company?.invoiceSettings?.enablePlatformFee && company.platformFee > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="font-medium text-gray-500 dark:text-gray-400">Platform Fee ({company.platformFee.toFixed(1)}%):</span>
                          <span className="font-medium text-gray-900 dark:text-gray-100">
                            ${((calculateTotals().subtotal + calculateTotals().taxAmount) * (company.platformFee / 100)).toFixed(2)}
                          </span>
                        </div>
                      )}
                      <div className="border-t pt-2 flex justify-between text-base">
                        <span className="font-semibold text-gray-700 dark:text-gray-300">Total:</span>
                        <span className="font-bold text-gray-900 dark:text-gray-100">${calculateTotals().total.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>

              {/* Notes and Footer Card */}
              <Card className="p-6 shadow-sm border border-gray-200/50 dark:border-gray-700/50 bg-white dark:bg-gray-800">
                <div className="space-y-4">
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 flex items-center">
                    <span className="inline-block w-1.5 h-5 bg-teal-500 rounded-sm mr-2"></span>
                    Notes & Additional Information
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Notes (visible to customer)</Label>
                      <RichTextEditor
                        value={formData.notes}
                        onChange={(html) => setFormData(prev => ({ ...prev, notes: html }))}
                        placeholder="Enter any notes to display on the invoice..."
                        minHeight="120px"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Footer Text</Label>
                      <RichTextEditor
                        value={formData.footerText}
                        onChange={(html) => setFormData(prev => ({ ...prev, footerText: html }))}
                        placeholder="Enter any text to display at the bottom of the invoice..."
                        minHeight="120px"
                      />
                    </div>
                  </div>
                </div>
              </Card>
            </>
          )}
        </>
      )}

      {/* Add Customer Dialog */}
      <AddCustomerDialog 
        open={isAddCustomerDialogOpen} 
        onOpenChange={setIsAddCustomerDialogOpen}
        onCustomerCreated={(customer) => {
          // Set the newly created customer as selected
          setFormData(prev => ({ ...prev, customerId: customer.id }));
          setCustomerSearchTerm(customer.companyName || `${customer.firstName} ${customer.lastName}`);
          // Add first line item if none exist
          if (formData.items.length === 0 && !isEditing.isEdit && !isEditingTemplate) {
            addLineItem();
          }
        }}
      />

      {/* Email Dialog */}
      <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Email Invoice</DialogTitle>
          </DialogHeader>
          {/* Keep the existing email form as is, but you might want to improve styling */}
        </DialogContent>
      </Dialog>

      {/* Add this after the customer details section */}
      <PaymentMethodsSection
        paymentMethods={formData.paymentMethods}
        autoPayEnabled={formData.autoPayEnabled}
        onPaymentMethodChange={handlePaymentMethodChange}
        onAutoPayChange={handleAutoPayChange}
        achAvailable={achAvailable && !!company?.invoiceSettings?.paymentMethods?.ach}
      />

      {/* Add Product Dialog */}
      <AddEditProductDialog
        isOpen={isAddProductDialogOpen}
        onOpenChange={(open) => {
          setIsAddProductDialogOpen(open);
          if (!open) {
            setPreselectedCategoryForProduct(undefined);
            setCurrentLineItemIndex(null);
          }
        }}
        companyId={user?.companyId || ''}
        onAddCategoryOpen={() => setIsAddCategoryDialogOpen(true)}
        preselectedCategoryId={preselectedCategoryForProduct}
        onProductCreated={handleProductCreated}
      />

      {/* Add Category Dialog */}
      <AddCategoryDialog
        isOpen={isAddCategoryDialogOpen}
        onOpenChange={(open) => {
          setIsAddCategoryDialogOpen(open);
          if (!open) {
            setCurrentLineItemIndex(null);
          }
        }}
        companyId={user?.companyId || ''}
        onCategoryCreated={handleCategoryCreated}
      />
    </div>
  );
} 
